// src/pages/subjects/student/SubjectStudentContent.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import ReactMarkdown from "react-markdown";
import ReactPlayer from "react-player/youtube";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coy as prismBaseStyle } from "react-syntax-highlighter/dist/esm/styles/prism";
import copy from "copy-to-clipboard";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";

import SubjectExp1Png from "../../../assets/images/subject-exp1-image.png";
import subjectArrow from "../../../Logos To be used/subject arrow.svg";
import "../../../styles/SubjectRightVar1.css";
import "../../../styles/LessonContentTypography.css";

import {
  ALTKLAUSUREN_QUERY,
  CREATE_ENROLLMENT_MUTATION,
  CREATE_LESSON_ENROLLMENT_MUTATION,
  LESSON_CONTENTS_QUERY,
  LESSON_TASKS_QUERY,
  MARK_LESSON_COMPLETED_MUTATION,
  QUIZ_ATTEMPTS_BY_USER,
  QUIZZES_QUERY,
  SUBJECT_DETAIL_BLOCKS_QUERY,
  SUBJECT_AI_CHATS_BY_USER_QUERY,
  SUBJECT_QUERY,
  USER_NAME_QUERY,
  SUBMIT_LESSON_FEEDBACK_MUTATION,
  SUBMIT_QUIZ_ATTEMPT_MUTATION,
  SAVE_ALTKLAUSUR_QUIZ_SESSION_ANSWER_MUTATION,
  START_ALTKLAUSUR_QUIZ_SESSION_MUTATION,
} from "./studentSubjectGraphql";
import {
  AltklausurQuizCarousel,
  GeneratedQuizPreview,
  LessonTaskCarousel,
  QuizBlock,
  StudentGenerateButton,
  isMultipleQuizType,
  isRequiredQuiz,
  sortByPositionThenId,
} from "./studentPracticeComponents";
import {
  generateKlausurPraxis,
  generateLessonQuiz,
  generateSimilarAufgabe,
  persistGeneratedAufgabe,
  sendSubjectChat,
} from "./learnhutAiClient";

import { installSavedFormulaRenderer } from "../../../utils/renderSavedFormulas";
import { normalizeRichHtmlForDisplay } from "../../../utils/richContent";
import { uploadImageToCloudinary } from "../../../utils/cloudinaryUpload";
import {
  getDeletedLessonContentIds,
} from "../../../utils/lessonContentVisibility";
import {
  WEEKLY_PROFESSOR_QUIZ_ALTKLAUSUR_ID,
  GRUNDLAGEN_ELEKTROTECHNIK_2_SUBJECT_ID,
  TECHNISCHE_MECHANIK_1_SUBJECT_ID,
  getAltKlausurenPracticeLabel,
  getLessonPracticeDefaultTab,
  isKlausurPraxisSubject,
  isSimilarAufgabenSubject,
  isWeeklyProfessorQuizSubject,
  orderWeeklyProfessorQuestions,
} from "../exceptions/subjectExceptions";


import Feedback1 from "../../../Logos To be used/Feedback 1.png";
import Feedback2 from "../../../Logos To be used/Feedback 2.png";
import Feedback3 from "../../../Logos To be used/Feedback 3.png";
import Feedback4 from "../../../Logos To be used/Feedback 4.png";
import Feedback5 from "../../../Logos To be used/Feedback 5.png";

const offlineImages = {
  "subject-exp1-image.png": SubjectExp1Png,
};

const customCodeStyle = {
  ...prismBaseStyle,
  "code[class*='language-']": {
    ...prismBaseStyle["code[class*='language-']"],
    background: "#EEEEEE",
  },
  "pre[class*='language-']": {
    ...prismBaseStyle["pre[class*='language-']"],
    background: "#EEEEEE",
    border: "1px solid #999999",
    borderRadius: "5px",
    padding: "1em",
  },
};

const AI_PREVIEW_BULLETS = [
  "Fragen zum aktuellen Fach stellen",
  "PDFs, Bilder und Notizen als Kontext hochladen",
  "Hinweise oder vollständige Lösungswege anfordern",
];

const AI_CHAT_SUGGESTIONS = [
  "Gib mir einen Hinweis zu dieser Lektion.",
  "Bitte zeige mir die vollständige Lösung Schritt für Schritt.",
  "Erkläre mir die wichtigsten Formeln.",
];

const AI_UPLOAD_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.txt,.md,.csv,.json,.tex";
const AI_UPLOAD_LIMIT = 6;
const AI_CHAT_MAX_INTERACTIONS = 20;
const AI_CHAT_MAX_MESSAGES = AI_CHAT_MAX_INTERACTIONS * 2;
const AI_UPLOAD_EXTENSIONS = new Set(
  AI_UPLOAD_ACCEPT.split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
);
const AI_PERSIST_CHAT = process.env.REACT_APP_LEARNHUT_AI_PERSIST_CHAT !== "false";

const AI_CHAT_WAIT_STEPS = [
  "Details sammeln",
  "Werte prüfen",
  "Berechnungen durchführen",
  "Antwort vorbereiten",
];

const AI_GENERATION_WAIT_STEPS = {
  quiz: [
    "Lektionsinhalt lesen",
    "Vorhandene Quizfragen prüfen",
    "Antwortoptionen abstimmen",
    "Quizfragen vorbereiten",
  ],
  klausur: [
    "Kursmaterial sichten",
    "Schwierigkeiten mischen",
    "Antwortoptionen prüfen",
    "Klausur speichern",
  ],
  sharedKlausur: [
    "Manuelle Aufgaben und Altklausuren sammeln",
    "Bildhinweise und Musterlösungen prüfen",
    "Klausurstruktur planen",
    "Neue Aufgaben formulieren",
    "Rechenwege und Musterlösungen vorbereiten",
    "Skizzen und Bilder abstimmen",
    "Klausur in der Datenbank speichern",
    "Fast fertig, das kann insgesamt ca. 7-8 Minuten dauern",
  ],
  aufgabe: [
    "Aufgabe analysieren",
    "Werte prüfen",
    "Variante berechnen",
    "Aufgabe vorbereiten",
  ],
};
const AI_SIMILAR_AUFGABEN_LOCK_TITLE =
  "Ähnliche Aufgaben sind aktuell nur für Technische Mechanik 1 und Grundlagen Elektrotechnik 2 aktiv.";
const AI_SIMILAR_AUFGABEN_MANUAL_ONLY_TITLE =
  "Ähnliche Aufgaben können nur aus manuell eingetragenen Aufgaben generiert werden.";
const KLAUSUR_PRAXIS_QUESTION_COUNT = 20;
const KLAUSUR_PRAXIS_MIN_OPTIONS = 3;
const KLAUSUR_PRAXIS_MAX_OPTIONS = 5;
const SHARED_KLAUSUR_GENERATION_EVENT = "learnhut:shared-klausur-generation";
const SHARED_KLAUSUR_GENERATION_STORAGE_PREFIX = "learnhut:shared-klausur-generation:";

const SHARED_KLAUSUR_GENERATION_CONFIG = {
  [GRUNDLAGEN_ELEKTROTECHNIK_2_SUBJECT_ID]: {
    mode: "shared_altklausur_exercises",
    label: "GET II Klausur",
    exercise_count_min: 3,
    exercise_count_max: 5,
    exercise_count_policy: "random_between_3_and_5",
    exercise_mix: "course_representative_random",
    expected_duration_minutes: "7-8",
  },
  [TECHNISCHE_MECHANIK_1_SUBJECT_ID]: {
    mode: "shared_altklausur_exercises",
    label: "TMI Klausur",
    exercise_count: 5,
    big_exercise_count: 2,
    small_exercise_count: 3,
    exercise_count_policy: "exactly_5_two_big_three_small",
    exercise_mix: "2_big_3_small",
    expected_duration_minutes: "7-8",
  },
};

const AI_SUBJECTS = [
  {
    id: "allgemeine_psychologie_i",
    title: "Allgemeine Psychologie I",
    generation: "quiz",
    goSubjectIds: ["960e37de-5054-444a-8763-897a07178b2f"],
  },
  {
    id: "handels_und_gesellschaftsrecht",
    title: "Handels- und Gesellschaftsrecht",
    generation: "quiz",
    goSubjectIds: ["d2c5857d-4d6c-4e1d-aaa5-a6a0875cac40"],
  },
  {
    id: "technische_mechanik_1",
    title: "Technische Mechanik 1",
    generation: "aufgabe",
    goSubjectIds: ["385999b9-532a-4526-8ba4-40c86f47a0f2"],
  },
  {
    id: "grundlagen_elektrotechnik_2",
    title: "Grundlagen Elektrotechnik 2",
    generation: "aufgabe",
    goSubjectIds: ["94ce3248-7a00-40be-b5f6-8c4f831ad526"],
  },
];

const normalizeAiSubjectTitle = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, "und")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getAiSubjectConfig = (subjectTitle, goSubjectId) => {
  const normalizedGoSubjectId = String(goSubjectId || "").trim().toLowerCase();
  if (normalizedGoSubjectId) {
    const byGoId = AI_SUBJECTS.find((subject) =>
      (subject.goSubjectIds || []).some((id) => id.toLowerCase() === normalizedGoSubjectId)
    );
    if (byGoId) return byGoId;
  }

  const normalized = normalizeAiSubjectTitle(subjectTitle);
  return (
    AI_SUBJECTS.find((subject) => normalizeAiSubjectTitle(subject.title) === normalized) ||
    AI_SUBJECTS.find((subject) => normalized.includes(normalizeAiSubjectTitle(subject.title))) ||
    null
  );
};

const toLessonContentPayload = (contents) =>
  (contents || []).map((content, index) => ({
    id: content.id,
    content_type: content.contentType,
    content_data: content.contentData || "",
    position: Number.isFinite(Number(content.position)) ? Number(content.position) : index,
  }));

const toExistingQuizPayload = (quizzes, optionsById) =>
  (quizzes || []).map((quiz) => ({
    id: quiz.id,
    question: quiz.question || "",
    options: (optionsById?.[quiz.id] || []).map((option) => option.optionText || ""),
  }));

const taskField = (task, camelName, snakeName, fallback = "") =>
  task?.[camelName] ?? task?.[snakeName] ?? fallback;

const getTaskId = (task) => String(task?.id || "");

const isAiGeneratedTask = (task) => {
  const sourceType = String(taskField(task, "sourceType", "source_type", "")).toLowerCase();
  return sourceType === "ai" || Boolean(taskField(task, "generatedForUserID", "generated_for_user_id", ""));
};

const getAltklausurSourceType = (alt) =>
  String(alt?.sourceType || alt?.source_type || "").toLowerCase();

const getAltklausurGeneratedForUserId = (alt) =>
  alt?.generatedForUserId || alt?.generated_for_user_id || null;

const getAltklausurCreatorId = (alt) => alt?.createdBy || alt?.created_by || null;

const isAiGeneratedAltklausur = (alt) =>
  getAltklausurSourceType(alt) === "ai" || Boolean(getAltklausurGeneratedForUserId(alt));

const isManualTaskForKlausurContext = (task) => !isAiGeneratedTask(task);

const getSharedKlausurGenerationConfig = (subjectId) => {
  const normalized = String(subjectId || "").toLowerCase();
  return SHARED_KLAUSUR_GENERATION_CONFIG[normalized] || null;
};

const getSharedKlausurGenerationStorageKey = (subjectId, userId) =>
  subjectId && userId
    ? `${SHARED_KLAUSUR_GENERATION_STORAGE_PREFIX}${subjectId}:${userId}`
    : "";

const readSharedKlausurPending = (storageKey) => {
  if (!storageKey || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeSharedKlausurPending = (storageKey, payload) => {
  if (!storageKey || typeof window === "undefined") return;
  window.localStorage.setItem(storageKey, JSON.stringify(payload));
  window.dispatchEvent(
    new CustomEvent(SHARED_KLAUSUR_GENERATION_EVENT, {
      detail: { storageKey, status: payload?.status || "pending", payload },
    })
  );
};

const clearSharedKlausurPending = (storageKey, status = "completed") => {
  if (!storageKey || typeof window === "undefined") return;
  window.localStorage.removeItem(storageKey);
  window.dispatchEvent(
    new CustomEvent(SHARED_KLAUSUR_GENERATION_EVENT, {
      detail: { storageKey, status },
    })
  );
};

const htmlToPlainText = (html = "") => {
  if (!html || typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  return (doc.body?.textContent || "").replace(/\s+/g, " ").trim();
};

const extractHtmlComments = (wrapper) => {
  if (!wrapper || !wrapper.ownerDocument?.createTreeWalker) return [];
  const comments = [];
  const showComment = typeof NodeFilter !== "undefined" ? NodeFilter.SHOW_COMMENT : 128;
  const walker = wrapper.ownerDocument.createTreeWalker(wrapper, showComment);
  let node = walker.nextNode();
  while (node) {
    const value = String(node.nodeValue || "").trim();
    if (value) comments.push(value);
    node = walker.nextNode();
  }
  return comments;
};

const extractTaskImageAssets = (html = "") => {
  if (!html || typeof DOMParser === "undefined") return [];
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const wrapper = doc.body.firstElementChild;
  if (!wrapper) return [];

  const comments = extractHtmlComments(wrapper);
  return Array.from(wrapper.querySelectorAll("img[src]")).map((img, index) => ({
    index,
    src: img.getAttribute("src") || "",
    alt: img.getAttribute("alt") || "",
    title: img.getAttribute("title") || "",
    width: img.getAttribute("width") || "",
    height: img.getAttribute("height") || "",
    layout: img.getAttribute("data-lh-image-layout") || "",
    class_name: img.getAttribute("class") || "",
    nearby_metadata: comments,
  }));
};

const toExistingTaskPayload = (tasks) =>
  (tasks || []).map((task, index) => {
    const taskText = taskField(task, "taskText", "task_text", "");
    const answerText = taskField(task, "answerText", "answer_text", "");
    return {
      id: task?.id || "",
      origin_task_id: taskField(task, "originTaskID", "origin_task_id", null),
      generated_for_user_id: taskField(task, "generatedForUserID", "generated_for_user_id", null),
      title: task?.title || "",
      task_text: taskText,
      task_text_plain: htmlToPlainText(taskText),
      answer_text: answerText,
      answer_text_plain: htmlToPlainText(answerText),
      hint_text: taskField(task, "hintText", "hint_text", ""),
      source_type: taskField(task, "sourceType", "source_type", ""),
      difficulty: task?.difficulty || "",
      position: Number.isFinite(Number(task?.position)) ? Number(task.position) : index,
      estimated_minutes: taskField(task, "estimatedMinutes", "estimated_minutes", null),
      is_published: task?.isPublished ?? task?.is_published ?? true,
      created_by: taskField(task, "createdBy", "created_by", null),
      image_assets: extractTaskImageAssets(taskText),
      answer_image_assets: extractTaskImageAssets(answerText),
    };
  });

const toManualExistingTaskPayload = (tasks) =>
  toExistingTaskPayload((tasks || []).filter(isManualTaskForKlausurContext));

const toExistingAltklausurPayload = (altklausuren) =>
  (altklausuren || [])
    .filter((alt) => alt?.isPublished !== false)
    .filter((alt) => !isAiGeneratedAltklausur(alt))
    .map((alt) => ({
      id: alt?.id || "",
      title: alt?.title || "",
      description: alt?.description || "",
      source_type: alt?.sourceType || alt?.source_type || "official",
      versions: (alt?.versions || [])
        .filter((version) => version?.isPublished !== false)
        .map((version) => ({
          id: version?.id || "",
          title: version?.title || "",
          description: version?.description || "",
          version_date: version?.versionDate || version?.version_date || "",
          source_type: version?.sourceType || version?.source_type || "official",
          tasks: toManualExistingTaskPayload(version?.tasks || []),
          quiz_questions: (version?.quizQuestions || []).map((question, index) => ({
            id: question?.id || "",
            position: Number.isFinite(Number(question?.position)) ? Number(question.position) : index,
            question_text: question?.questionText || question?.question_text || "",
            question_type: question?.questionType || question?.question_type || "single_choice",
            explanation: question?.explanation || "",
            hint: question?.hint || "",
            points: Math.max(1, Number(question?.points || 1)),
            options: (question?.options || []).map((option, optionIndex) => ({
              id: option?.id || "",
              position: Number.isFinite(Number(option?.position)) ? Number(option.position) : optionIndex,
              option_text: option?.optionText || option?.option_text || "",
            })),
          })),
        })),
    }));

const toLessonTaskContextPayload = (lessonTaskGroups) =>
  (lessonTaskGroups || []).map(({ lesson, tasks }) => ({
    lesson_id: lesson?.id || "",
    lesson_title: lesson?.title || "",
    lesson_slug: lesson?.slug || "",
    tasks: toManualExistingTaskPayload(tasks || []),
  }));


const dataUrlToImageFile = async (dataUrl, filename = "learnhut-generated-aufgabe.png") => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const extension = (blob.type || "image/png").split("/")[1] || "png";
  const safeName = filename.includes(".") ? filename : `${filename}.${extension}`;
  return new File([blob], safeName, { type: blob.type || "image/png" });
};

const getGeneratedImageSource = (task, result) => {
  const image = task?.image || result?.image || {};
  return (
    task?.imageDataUrl ||
    task?.image_data_url ||
    task?.generatedImageDataUrl ||
    task?.generated_image_data_url ||
    image?.dataUrl ||
    image?.data_url ||
    image?.url ||
    result?.imageDataUrl ||
    result?.image_data_url ||
    result?.generatedImageDataUrl ||
    result?.generated_image_data_url ||
    result?.diagnostics?.generated_image_data_url ||
    result?.diagnostics?.generated_image_url ||
    ""
  );
};

const ensureGeneratedImageInHtml = (html = "", imageSource = "", alt = "Generierte Skizze") => {
  if (!imageSource || /<img\b/i.test(html)) return html;
  return `<p><img src="${imageSource}" alt="${alt}" data-lh-image-layout="center" class="lh6-image-align-center" width="720"></p>${html}`;
};

const uploadEmbeddedTaskImages = async (html = "") => {
  if (!html || typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const wrapper = doc.body.firstElementChild;
  if (!wrapper) return html;

  const images = Array.from(wrapper.querySelectorAll("img[src]"));
  for (const img of images) {
    const src = img.getAttribute("src") || "";
    if (!src.startsWith("data:image/")) continue;

    const file = await dataUrlToImageFile(src, img.getAttribute("alt") || "learnhut-generated-aufgabe");
    const cloudinaryUrl = await uploadImageToCloudinary(file);
    img.setAttribute("src", cloudinaryUrl);
  }

  return wrapper.innerHTML;
};

const isAiGeneratedQuiz = (quiz) => {
  const sourceType = String(quiz?.sourceType || quiz?.source_type || "").toLowerCase();
  return sourceType === "ai" || Boolean(quiz?.generatedForUserId || quiz?.generated_for_user_id);
};

const normalizeGeneratedTaskPreview = (task, index, lessonId, sourceTask) => ({
  id: task?.id || `ai-task-${Date.now()}-${index}`,
  lessonID: task?.lessonID || task?.lesson_id || lessonId,
  originTaskID:
    task?.originTaskID ||
    task?.origin_task_id ||
    taskField(sourceTask, "originTaskID", "origin_task_id", null) ||
    sourceTask?.id ||
    null,
  generatedForUserID: task?.generatedForUserID || task?.generated_for_user_id || null,
  title: task?.title || "Generierte Aufgabe",
  taskText: task?.taskText || task?.task_text || "",
  hintText: task?.hintText || task?.hint_text || "",
  answerText: task?.answerText || task?.answer_text || "",
  position: Number.isFinite(Number(task?.position)) ? Number(task.position) : index,
  difficulty: task?.difficulty || "",
  estimatedMinutes: task?.estimatedMinutes || task?.estimated_minutes || null,
  sourceType: task?.sourceType || task?.source_type || "ai",
  isPublished: task?.isPublished ?? task?.is_published ?? false,
});

const prepareGeneratedTaskPreview = async (task, result, index, lessonId, sourceTask) => {
  const imageSource = getGeneratedImageSource(task, result);
  const taskText = ensureGeneratedImageInHtml(
    task?.taskText || task?.task_text || "",
    imageSource,
    task?.title ? `Skizze zu ${task.title}` : "Generierte Skizze"
  );
  const answerText = task?.answerText || task?.answer_text || "";
  const taskTextWithCloudinaryImages = await uploadEmbeddedTaskImages(taskText);
  const answerTextWithCloudinaryImages = await uploadEmbeddedTaskImages(answerText);

  return normalizeGeneratedTaskPreview(
    {
      ...task,
      taskText: taskTextWithCloudinaryImages,
      task_text: taskTextWithCloudinaryImages,
      answerText: answerTextWithCloudinaryImages,
      answer_text: answerTextWithCloudinaryImages,
    },
    index,
    lessonId,
    sourceTask
  );
};

const shouldFinalizeGeneratedTaskAfterImageUpload = (result) =>
  Boolean(
    result?.requires_frontend_image_upload ||
      result?.needs_frontend_image_upload ||
      result?.persistence_pending ||
      result?.persistence?.status === "awaiting_frontend_upload"
  );

const toGeneratedTaskPersistPayload = (task, sourceTask, lessonId, userId) => ({
  id: task?.id || null,
  lesson_id: task?.lessonID || task?.lesson_id || lessonId,
  origin_task_id: task?.originTaskID || task?.origin_task_id || sourceTask?.id || null,
  generated_for_user_id: task?.generatedForUserID || task?.generated_for_user_id || userId || null,
  title: task?.title || "Generierte Aufgabe",
  task_text: task?.taskText || task?.task_text || "",
  hint_text: task?.hintText || task?.hint_text || null,
  answer_text: task?.answerText || task?.answer_text || null,
  position: Number.isFinite(Number(task?.position)) ? Number(task.position) : 0,
  difficulty: task?.difficulty || null,
  estimated_minutes: task?.estimatedMinutes || task?.estimated_minutes || null,
  source_type: task?.sourceType || task?.source_type || "ai",
  is_published: task?.isPublished ?? task?.is_published ?? false,
});

const normalizeGeneratedQuizPreview = (quiz, index, lessonId) => ({
  id: quiz?.id || `ai-quiz-${Date.now()}-${index}`,
  lessonId,
  question: quiz?.question || quiz?.questionText || quiz?.question_text || "",
  questionType: quiz?.questionType || quiz?.question_type || "single_choice",
  options: (quiz?.options || []).map((option, optionIndex) =>
    typeof option === "string"
      ? {
          id: `ai-quiz-option-${index}-${optionIndex}`,
          optionText: option,
          position: optionIndex,
        }
      : {
          id: option?.id || `ai-quiz-option-${index}-${optionIndex}`,
          optionText: option?.optionText || option?.option_text || option?.text || "",
          position: Number.isFinite(Number(option?.position)) ? Number(option.position) : optionIndex,
        }
  ),
});

const isCorrectOption = (option) =>
  option?.isCorrect === true || option?.is_correct === true;

const toOptionIds = (value) =>
  Array.isArray(value) ? value.map((id) => String(id || "")).filter(Boolean) : [];

const normalizeGeneratedKlausurQuestion = (question, index) => {
  const rawOptions = question?.options || question?.answers || [];
  const correctIndex = Number(question?.correct_option_index ?? question?.correctOptionIndex);

  return {
    id: question?.id || `temp-klausur-question-${Date.now()}-${index}`,
    position: Number.isFinite(Number(question?.position)) ? Number(question.position) : index,
    questionText: question?.questionText || question?.question_text || question?.question || "",
    questionType: question?.questionType || question?.question_type || "single_choice",
    explanation: question?.explanation || "",
    hint: question?.hint || "",
    points: Math.max(1, Number(question?.points || 1)),
    options: rawOptions.map((option, optionIndex) => {
      if (typeof option === "string") {
        return {
          id: `temp-klausur-option-${index}-${optionIndex}`,
          position: optionIndex,
          optionText: option,
          isCorrect: Number.isFinite(correctIndex) && correctIndex === optionIndex,
        };
      }

      return {
        id: option?.id || `temp-klausur-option-${index}-${optionIndex}`,
        position: Number.isFinite(Number(option?.position)) ? Number(option.position) : optionIndex,
        optionText: option?.optionText || option?.option_text || option?.text || "",
        isCorrect:
          typeof option?.isCorrect === "boolean"
            ? option.isCorrect
            : typeof option?.is_correct === "boolean"
            ? option.is_correct
            : Number.isFinite(correctIndex) && correctIndex === optionIndex,
      };
    }),
  };
};

const normalizeGeneratedKlausurTask = (task, index) => ({
  id: task?.id || `temp-klausur-task-${Date.now()}-${index}`,
  originTaskID: task?.originTaskID || task?.origin_task_id || null,
  generatedForUserID: task?.generatedForUserID || task?.generated_for_user_id || null,
  title: task?.title || task?.name || `Aufgabe ${index + 1}`,
  taskText: task?.taskText || task?.task_text || task?.content || "",
  hintText: task?.hintText || task?.hint_text || task?.hint || "",
  answerText: task?.answerText || task?.answer_text || task?.solution || task?.solution_html || "",
  position: Number.isFinite(Number(task?.position)) ? Number(task.position) : index,
  difficulty: task?.difficulty || "",
  estimatedMinutes: task?.estimatedMinutes || task?.estimated_minutes || null,
  sourceType: task?.sourceType || task?.source_type || "ai",
  isPublished: task?.isPublished ?? task?.is_published ?? true,
  createdBy: task?.createdBy || task?.created_by || null,
});

const getGeneratedAnswerImageSource = (task, result) => {
  const image = task?.answerImage || task?.answer_image || task?.solutionImage || task?.solution_image || {};
  return (
    task?.answerImageDataUrl ||
    task?.answer_image_data_url ||
    task?.solutionImageDataUrl ||
    task?.solution_image_data_url ||
    image?.dataUrl ||
    image?.data_url ||
    image?.url ||
    result?.answerImageDataUrl ||
    result?.answer_image_data_url ||
    result?.solutionImageDataUrl ||
    result?.solution_image_data_url ||
    ""
  );
};

const prepareGeneratedKlausurTaskPreview = async (task, result, index) => {
  const taskImageSource = getGeneratedImageSource(task, result);
  const answerImageSource = getGeneratedAnswerImageSource(task, result);
  const taskText = ensureGeneratedImageInHtml(
    task?.taskText || task?.task_text || task?.content || "",
    taskImageSource,
    task?.title ? `Skizze zu ${task.title}` : "Generierte Klausurskizze"
  );
  const answerText = ensureGeneratedImageInHtml(
    task?.answerText || task?.answer_text || task?.solution || task?.solution_html || "",
    answerImageSource,
    task?.title ? `Musterlösung zu ${task.title}` : "Generierte Musterlösung"
  );

  const taskTextWithCloudinaryImages = await uploadEmbeddedTaskImages(taskText);
  const answerTextWithCloudinaryImages = await uploadEmbeddedTaskImages(answerText);

  return normalizeGeneratedKlausurTask(
    {
      ...task,
      taskText: taskTextWithCloudinaryImages,
      task_text: taskTextWithCloudinaryImages,
      answerText: answerTextWithCloudinaryImages,
      answer_text: answerTextWithCloudinaryImages,
    },
    index
  );
};

const normalizeGeneratedKlausurPreview = (result, subjectId, userId, preparedTasks = null) => {
  const source = result?.altklausur || result?.klausur || result?.exam || {};
  const sourceVersion = source?.versions?.[0] || result?.version || {};
  const questions =
    result?.questions ||
    source?.quizQuestions ||
    sourceVersion?.quizQuestions ||
    sourceVersion?.questions ||
    [];
  const rawTasks =
    result?.tasks ||
    result?.exercises ||
    result?.aufgaben ||
    source?.tasks ||
    sourceVersion?.tasks ||
    [];
  const createdAtLabel = new Date().toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return {
    id: source?.id || `temp-klausur-${Date.now()}`,
    subjectID: source?.subjectID || source?.subject_id || subjectId,
    title: source?.title || result?.title || `Klausur Praxis ${createdAtLabel}`,
    description:
      source?.description ||
      result?.description ||
      "KI-generierte Klausur für den gemeinsamen Praxismodus.",
    isPublished: source?.isPublished ?? source?.is_published ?? true,
    isTemporary: !source?.id,
    sourceType: source?.sourceType || source?.source_type || result?.source_type || "ai",
    generatedForUserId:
      source?.generatedForUserId ||
      source?.generated_for_user_id ||
      result?.generated_for_user_id ||
      userId ||
      null,
    createdBy: source?.createdBy || source?.created_by || result?.created_by || userId || "",
    createdByName: source?.createdByName || source?.created_by_name || result?.created_by_name || "",
    versions: [
      {
        id: sourceVersion?.id || `temp-klausur-version-${Date.now()}`,
        title: sourceVersion?.title || "Generierte Klausur",
        versionDate: sourceVersion?.versionDate || sourceVersion?.version_date || new Date().toISOString(),
        description: sourceVersion?.description || source?.description || result?.description || "",
        sourceType: sourceVersion?.sourceType || sourceVersion?.source_type || "ai",
        timeLimitSeconds: Number(sourceVersion?.timeLimitSeconds || sourceVersion?.time_limit_seconds || 0),
        isPublished: sourceVersion?.isPublished ?? sourceVersion?.is_published ?? true,
        isLocked: sourceVersion?.isLocked ?? sourceVersion?.is_locked ?? false,
        quizQuestions: questions.map(normalizeGeneratedKlausurQuestion),
        tasks: Array.isArray(preparedTasks)
          ? preparedTasks
          : rawTasks.map(normalizeGeneratedKlausurTask),
      },
    ],
  };
};

const prepareGeneratedKlausurPreview = async (result, subjectId, userId) => {
  const source = result?.altklausur || result?.klausur || result?.exam || {};
  const sourceVersion = source?.versions?.[0] || result?.version || {};
  const rawTasks =
    result?.tasks ||
    result?.exercises ||
    result?.aufgaben ||
    source?.tasks ||
    sourceVersion?.tasks ||
    [];
  const preparedTasks = await Promise.all(
    rawTasks.map((task, index) => prepareGeneratedKlausurTaskPreview(task, result, index))
  );
  return normalizeGeneratedKlausurPreview(result, subjectId, userId, preparedTasks);
};

const getKlausurPrimaryItemCount = (alt) => {
  const version = alt?.versions?.[0] || null;
  return (version?.tasks || []).length + (version?.quizQuestions || []).length;
};


const getChatAnswer = (response) =>
  response?.answer_markdown ||
  response?.answer_html ||
  response?.answer ||
  response?.message ||
  "";

const formatAiUploadSize = (file) => {
  const size = Number(file?.size || 0);
  if (!size) return "";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const aiUploadFingerprint = (file) =>
  `${file?.webkitRelativePath || file?.name || "upload"}-${file?.size || 0}-${file?.lastModified || 0}`;

const isAcceptedAiUpload = (file) => {
  const name = String(file?.name || "").toLowerCase();
  return Array.from(AI_UPLOAD_EXTENSIONS).some((extension) => name.endsWith(extension));
};

const mergeAiChatUploads = (current, incoming) => {
  const byFingerprint = new Map(
    (current || []).map((file) => [aiUploadFingerprint(file), file])
  );
  (incoming || [])
    .filter(isAcceptedAiUpload)
    .forEach((file) => {
      byFingerprint.set(aiUploadFingerprint(file), file);
    });
  return Array.from(byFingerprint.values()).slice(0, AI_UPLOAD_LIMIT);
};

const fileFromEntry = (entry) =>
  new Promise((resolve) => {
    entry.file(resolve, () => resolve(null));
  });

const readDirectoryEntries = (reader) =>
  new Promise((resolve) => {
    const entries = [];
    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (!batch.length) {
            resolve(entries);
            return;
          }
          entries.push(...batch);
          readBatch();
        },
        () => resolve(entries)
      );
    };
    readBatch();
  });

const filesFromDropEntry = async (entry) => {
  if (!entry) return [];
  if (entry.isFile) {
    const file = await fileFromEntry(entry);
    return file ? [file] : [];
  }
  if (entry.isDirectory) {
    const entries = await readDirectoryEntries(entry.createReader());
    const nested = await Promise.all(entries.map(filesFromDropEntry));
    return nested.flat();
  }
  return [];
};

const filesFromDataTransfer = async (dataTransfer) => {
  const items = Array.from(dataTransfer?.items || []);
  if (items.length > 0) {
    const files = await Promise.all(
      items
        .filter((item) => item.kind === "file")
        .map((item) => {
          const entry = item.webkitGetAsEntry?.();
          if (entry) return filesFromDropEntry(entry);
          const file = item.getAsFile?.();
          return Promise.resolve(file ? [file] : []);
        })
    );
    return files.flat();
  }
  return Array.from(dataTransfer?.files || []);
};

const safeReadAiChatCache = (key) => {
  if (!key) return { history: [], taskMemory: "" };
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "{}");
    return {
      history: Array.isArray(parsed.history) ? parsed.history : [],
      taskMemory: typeof parsed.taskMemory === "string" ? parsed.taskMemory : "",
    };
  } catch {
    return { history: [], taskMemory: "" };
  }
};

const normalizeAiChatEntry = (entry) => ({
  role: entry?.role === "assistant" ? "assistant" : "user",
  content: entry?.content || "",
  html: entry?.html || "",
  attachments: Array.isArray(entry?.attachments) ? entry.attachments : [],
  sources: Array.isArray(entry?.sources) ? entry.sources : [],
});

const trimAiChatHistory = (history) =>
  (history || [])
    .map(normalizeAiChatEntry)
    .filter((entry) => entry.content)
    .slice(-AI_CHAT_MAX_MESSAGES);

const renderMathError = (_, math) => <code>{math}</code>;

const parseInlineMath = (value, keyPrefix = "math") => {
  const text = String(value || "");
  const regex = /(\\\((.+?)\\\)|(?<!\$)\$(?!\$)([^$\n]+?)(?<!\$)\$(?!\$))/g;
  const nodes = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text))) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const math = (match[2] || match[3] || "").trim();
    nodes.push(
      <InlineMath
        key={`${keyPrefix}-${match.index}`}
        math={math}
        renderError={(error) => renderMathError(error, math)}
      />
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};

const renderInlineMath = (children) =>
  React.Children.toArray(children).flatMap((child, index) => {
    if (typeof child === "string") {
      return parseInlineMath(child, `inline-${index}`);
    }
    return child;
  });

const markdownWithMathComponents = {
  p: ({ node: _node, children, ...props }) => (
    <p {...props}>{renderInlineMath(children)}</p>
  ),
  li: ({ node: _node, children, ...props }) => (
    <li {...props}>{renderInlineMath(children)}</li>
  ),
  h1: ({ node: _node, children, ...props }) => (
    <h1 {...props}>{renderInlineMath(children)}</h1>
  ),
  h2: ({ node: _node, children, ...props }) => (
    <h2 {...props}>{renderInlineMath(children)}</h2>
  ),
  h3: ({ node: _node, children, ...props }) => (
    <h3 {...props}>{renderInlineMath(children)}</h3>
  ),
  h4: ({ node: _node, children, ...props }) => (
    <h4 {...props}>{renderInlineMath(children)}</h4>
  ),
  strong: ({ node: _node, children, ...props }) => (
    <strong {...props}>{renderInlineMath(children)}</strong>
  ),
  em: ({ node: _node, children, ...props }) => (
    <em {...props}>{renderInlineMath(children)}</em>
  ),
};

const splitMarkdownMathBlocks = (value) => {
  const markdown = String(value || "");
  const regex = /(?:^|\n)\s*(?:\$\$([\s\S]*?)\$\$|\\\[([\s\S]*?)\\\])\s*(?=\n|$)/g;
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(markdown))) {
    const segmentStart = match.index + (match[0].startsWith("\n") ? 1 : 0);
    if (segmentStart > lastIndex) {
      segments.push({ type: "markdown", value: markdown.slice(lastIndex, segmentStart) });
    }
    const math = (match[1] || match[2] || "").trim();
    if (math) {
      segments.push({ type: "math", value: math });
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < markdown.length) {
    segments.push({ type: "markdown", value: markdown.slice(lastIndex) });
  }

  return segments.length ? segments : [{ type: "markdown", value: markdown }];
};

const MarkdownMathContent = ({ content }) => (
  <div className="ai-chat-markdown">
    {splitMarkdownMathBlocks(content).map((segment, index) =>
      segment.type === "math" ? (
        <BlockMath
          key={`math-${index}`}
          math={segment.value}
          renderError={(error) => renderMathError(error, segment.value)}
        />
      ) : (
        <ReactMarkdown
          key={`markdown-${index}`}
          components={markdownWithMathComponents}
        >
          {segment.value}
        </ReactMarkdown>
      )
    )}
  </div>
);

const scrollElementIntoViewSlowly = (element) => {
  if (!element || typeof window === "undefined" || typeof document === "undefined") return;

  const scrollContainer =
    element.closest?.(".right-section") || document.scrollingElement || document.documentElement;
  const useWindow =
    scrollContainer === document.scrollingElement || scrollContainer === document.documentElement;
  const containerRect = useWindow ? { top: 0 } : scrollContainer.getBoundingClientRect();
  const startY = useWindow
    ? window.scrollY || scrollContainer.scrollTop || 0
    : scrollContainer.scrollTop || 0;
  const targetY = Math.max(
    0,
    startY + element.getBoundingClientRect().top - containerRect.top - 84
  );
  const setScrollTop = (top) => {
    if (useWindow) {
      window.scrollTo(0, top);
    } else {
      scrollContainer.scrollTop = top;
    }
  };

  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  if (reducedMotion) {
    setScrollTop(targetY);
    return;
  }

  const distance = targetY - startY;
  const duration = 1100;
  const startedAt = performance.now();

  const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  const step = (now) => {
    const progress = Math.min(1, (now - startedAt) / duration);
    setScrollTop(startY + distance * easeInOutCubic(progress));
    if (progress < 1) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
};

export default function SubjectExp1Right({
  subjectId,
  lessonId,
  lessonTitle,
  showAltKlausurenComing = false,
  showKlausurPraxis = false,
  lessons,
  lessonCategories = [],
  onLessonComplete,
  onLessonSelect,
}) {
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [copiedBlock, setCopiedBlock] = useState(null);
  const [quizState, setQuizState] = useState({});
  const [quizOptionsById, setQuizOptionsById] = useState({});
  const [practiceTab, setPracticeTab] = useState("aufgaben");
  const [activeTaskIndex, setActiveTaskIndex] = useState(0);
  const [altPracticeTab, setAltPracticeTab] = useState("aufgaben");
  const [activeAltTaskIndex, setActiveAltTaskIndex] = useState(0);
  const [activeAltQuizIndex, setActiveAltQuizIndex] = useState(0);
  const [selectedAltId, setSelectedAltId] = useState(null);
  const [selectedAltVersionId, setSelectedAltVersionId] = useState(null);
  const [weeklyQuizSelections, setWeeklyQuizSelections] = useState({});
  const [weeklyQuizEvaluated, setWeeklyQuizEvaluated] = useState({});
  const [weeklyQuizSession, setWeeklyQuizSession] = useState(null);
  const [weeklyQuizServerResults, setWeeklyQuizServerResults] = useState({});
  const [weeklyQuizCheckingId, setWeeklyQuizCheckingId] = useState("");
  const [weeklyQuizErrors, setWeeklyQuizErrors] = useState({});
  const [aiInfoOpen, setAiInfoOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(null);
  const [aiGenerationMessage, setAiGenerationMessage] = useState("");
  const [aiGenerationError, setAiGenerationError] = useState("");
  const [generatedQuizPreviews, setGeneratedQuizPreviews] = useState([]);
  const [generatedKlausurPreviews, setGeneratedKlausurPreviews] = useState([]);
  const [generatedTaskPreviews, setGeneratedTaskPreviews] = useState([]);
  const [altCreatorNames, setAltCreatorNames] = useState({});
  const [sharedKlausurPending, setSharedKlausurPending] = useState(null);
  const [aiChatInput, setAiChatInput] = useState("");
  const [aiChatHistory, setAiChatHistory] = useState([]);
  const [aiTaskMemory, setAiTaskMemory] = useState("");
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [aiChatError, setAiChatError] = useState("");
  const [aiChatUploads, setAiChatUploads] = useState([]);
  const [aiChatDropActive, setAiChatDropActive] = useState(false);
  const [aiChatWaitIndex, setAiChatWaitIndex] = useState(0);
  const [aiGenerationWaitIndex, setAiGenerationWaitIndex] = useState(0);
  const userId = localStorage.getItem("userID");
  const localUserName = localStorage.getItem("userName") || "";
  const client = useApolloClient();

  // ✅ Feedback
  const [feedbackRating, setFeedbackRating] = useState(null); // 1..5
  const [feedbackText, setFeedbackText] = useState("");
  const [showFeedbackToast, setShowFeedbackToast] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackClosedForLesson, setFeedbackClosedForLesson] = useState(false);
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const textareaRef = useRef(null);
  const contentRootRef = useRef(null);
  const practiceResultsRef = useRef(null);
  const aiChatFileInputRef = useRef(null);
  const aiChatHistoryEndRef = useRef(null);
  const [hiddenLessonContentIds, setHiddenLessonContentIds] = useState([]);

  // ✅ AI button must be hidden for tutors/admins (even in "student view" mode)
  const role = localStorage.getItem("userRole");
  const isTutorOrAdmin = role === "TUTOR" || role === "ADMIN";
  const isWeeklyProfessorException = isWeeklyProfessorQuizSubject(subjectId);
  const hasKlausurPraxis = isKlausurPraxisSubject(subjectId);
  const altKlausurenPageTitle = isWeeklyProfessorException
    ? "Wochenquiz"
    : getAltKlausurenPracticeLabel(subjectId);

  const { data: subjData, loading: subjLoading, error: subjError } = useQuery(
    SUBJECT_QUERY,
    {
      variables: { id: subjectId },
      skip: !subjectId,
      fetchPolicy: "network-only",
    }
  );

  const {
    data: blocksData,
    loading: blocksLoading,
    error: blocksError,
  } = useQuery(SUBJECT_DETAIL_BLOCKS_QUERY, {
    variables: { subjectId },
    skip: !subjectId || Boolean(lessonId) || showAltKlausurenComing || showKlausurPraxis,
    fetchPolicy: "network-only",
  });

  const {
    data: contentData,
    loading: contentLoading,
    error: contentError,
  } = useQuery(LESSON_CONTENTS_QUERY, {
    variables: { lessonId },
    skip: !lessonId,
    fetchPolicy: "network-only",
  });

  const { data: quizData, refetch: quizzesRefetch } = useQuery(QUIZZES_QUERY, {
    variables: { lessonId },
    skip: !lessonId,
    fetchPolicy: "network-only",
  });

  const { data: lessonTasksData, refetch: lessonTasksRefetch } = useQuery(LESSON_TASKS_QUERY, {
    variables: { lessonID: lessonId },
    skip: !lessonId,
    fetchPolicy: "network-only",
  });

  const {
    data: altData,
    loading: altLoading,
    error: altError,
    refetch: altRefetch,
  } = useQuery(ALTKLAUSUREN_QUERY, {
    variables: subjectId ? { subjectID: subjectId } : undefined,
    skip: !subjectId || (!showAltKlausurenComing && !showKlausurPraxis),
    fetchPolicy: "network-only",
  });

  const { data: attemptsData, refetch: attemptsRefetch } = useQuery(
    QUIZ_ATTEMPTS_BY_USER,
    {
      variables: { userId },
      skip: !userId,
      fetchPolicy: "network-only",
    }
  );

  const [submitQuizAttempt] = useMutation(SUBMIT_QUIZ_ATTEMPT_MUTATION);
  const [startWeeklyQuizSession] = useMutation(START_ALTKLAUSUR_QUIZ_SESSION_MUTATION);
  const [saveWeeklyQuizSessionAnswer] = useMutation(SAVE_ALTKLAUSUR_QUIZ_SESSION_ANSWER_MUTATION);
  const [markLessonAsCompleted] = useMutation(MARK_LESSON_COMPLETED_MUTATION);
  const [createEnrollment] = useMutation(CREATE_ENROLLMENT_MUTATION);
  const [createLessonEnrollment] = useMutation(CREATE_LESSON_ENROLLMENT_MUTATION);
  const [submitLessonFeedback, { loading: feedbackSubmitting }] = useMutation(
    SUBMIT_LESSON_FEEDBACK_MUTATION
  );
  const effectiveQuizzes = useMemo(
    () => (quizData?.quizzesByLesson || []).slice().sort(sortByPositionThenId),
    [quizData]
  );
  const lessonTasks = useMemo(
    () => (lessonTasksData?.lessonTasks || []).slice().sort(sortByPositionThenId),
    [lessonTasksData]
  );
  const lessonTaskItems = useMemo(
    () => [...lessonTasks, ...generatedTaskPreviews],
    [generatedTaskPreviews, lessonTasks]
  );
  const defaultPracticeTab = useMemo(
    () => getLessonPracticeDefaultTab(subjectId, lessonTaskItems.length),
    [lessonTaskItems.length, subjectId]
  );
  const activeLessonTask = lessonTaskItems[activeTaskIndex] || lessonTaskItems[0] || null;
  const activeManualLessonTask =
    activeLessonTask && !isAiGeneratedTask(activeLessonTask) ? activeLessonTask : null;
  const currentAiSubject = useMemo(
    () => getAiSubjectConfig(subjData?.subject?.title, subjectId),
    [subjData, subjectId]
  );
  const sharedKlausurConfig = useMemo(
    () => getSharedKlausurGenerationConfig(subjectId),
    [subjectId]
  );
  const sharedKlausurStorageKey = useMemo(
    () => getSharedKlausurGenerationStorageKey(subjectId, userId),
    [subjectId, userId]
  );
  const canUseSubjectChat = Boolean(currentAiSubject);
  const { data: aiChatDbData } = useQuery(SUBJECT_AI_CHATS_BY_USER_QUERY, {
    variables: { userID: userId || "" },
    skip: !aiInfoOpen || !userId || !subjectId || !canUseSubjectChat,
    fetchPolicy: "network-only",
  });
  const aiChatStorageKey = useMemo(() => {
    if (!currentAiSubject?.id || !subjectId) return "";
    return `learnhut-ai-chat:${userId || "anonymous"}:${subjectId}:${currentAiSubject.id}`;
  }, [currentAiSubject, subjectId, userId]);
  const aiGenerationStepLabel = useMemo(() => {
    if (!aiGenerating) return "";
    const steps = AI_GENERATION_WAIT_STEPS[aiGenerating] || AI_GENERATION_WAIT_STEPS.quiz;
    return steps[Math.min(aiGenerationWaitIndex, steps.length - 1)] || steps[0];
  }, [aiGenerating, aiGenerationWaitIndex]);
  const aiChatStepLabel =
    AI_CHAT_WAIT_STEPS[Math.min(aiChatWaitIndex, AI_CHAT_WAIT_STEPS.length - 1)] ||
    AI_CHAT_WAIT_STEPS[0];

  useEffect(() => {
    setSharedKlausurPending(readSharedKlausurPending(sharedKlausurStorageKey));
    if (!sharedKlausurStorageKey || typeof window === "undefined") return undefined;

    const handleGenerationEvent = (event) => {
      if (event?.detail?.storageKey !== sharedKlausurStorageKey) return;
      setSharedKlausurPending(readSharedKlausurPending(sharedKlausurStorageKey));
      if (event.detail.status === "completed") {
        altRefetch?.();
      }
    };

    window.addEventListener(SHARED_KLAUSUR_GENERATION_EVENT, handleGenerationEvent);
    return () => window.removeEventListener(SHARED_KLAUSUR_GENERATION_EVENT, handleGenerationEvent);
  }, [altRefetch, sharedKlausurStorageKey]);

  const publishedAltklausuren = useMemo(() => {
    return (altData?.altklausuren || [])
      .filter((alt) => alt?.isPublished !== false)
      .map((alt) => {
        const primaryVersion = (alt?.versions || [])
          .filter((version) => version?.isPublished !== false)
          .slice()
          .sort((a, b) => {
            const dateA = a?.versionDate ? new Date(a.versionDate).getTime() : 0;
            const dateB = b?.versionDate ? new Date(b.versionDate).getTime() : 0;
            if (dateA !== dateB) return dateB - dateA;
            return String(a?.title || "").localeCompare(String(b?.title || ""));
          })
          .map((version) => ({
            ...version,
            quizQuestions: (version?.quizQuestions || []).slice().sort(sortByPositionThenId),
            tasks: (version?.tasks || [])
              .filter((task) => task?.isPublished !== false)
              .slice()
              .sort(sortByPositionThenId),
          }))[0];

        return {
          ...alt,
          versions: primaryVersion ? [primaryVersion] : [],
        };
      })
      .filter((alt) => alt.versions.length > 0)
      .sort((a, b) => String(a?.title || "").localeCompare(String(b?.title || "")));
  }, [altData]);

  const visibleAltklausuren = useMemo(() => {
    const seen = new Set();
    return [...generatedKlausurPreviews, ...publishedAltklausuren]
      .filter((alt) => alt?.isPublished !== false)
      .filter((alt) => {
        const key = String(alt?.id || "");
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => {
        if (a?.isTemporary && !b?.isTemporary) return -1;
        if (!a?.isTemporary && b?.isTemporary) return 1;
        return String(a?.title || "").localeCompare(String(b?.title || ""));
      });
  }, [generatedKlausurPreviews, publishedAltklausuren]);

  useEffect(() => {
    const creatorIds = Array.from(
      new Set(
        visibleAltklausuren
          .filter(isAiGeneratedAltklausur)
          .map(getAltklausurCreatorId)
          .filter(Boolean)
      )
    ).filter((id) => !altCreatorNames[id]);

    if (creatorIds.length === 0) return undefined;

    let cancelled = false;
    Promise.all(
      creatorIds.map((id) =>
        client
          .query({
            query: USER_NAME_QUERY,
            variables: { id },
            fetchPolicy: "cache-first",
          })
          .then((result) => [id, result?.data?.user?.name || ""])
          .catch(() => [id, ""])
      )
    ).then((entries) => {
      if (cancelled) return;
      setAltCreatorNames((prev) => {
        const next = { ...prev };
        entries.forEach(([id, name]) => {
          next[id] = name || (String(id) === String(userId) ? localUserName : "");
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [altCreatorNames, client, localUserName, userId, visibleAltklausuren]);

  const selectedAltklausur = useMemo(
    () =>
      visibleAltklausuren.find((alt) => alt.id === selectedAltId) || null,
    [visibleAltklausuren, selectedAltId]
  );

  const selectedAltVersion = useMemo(() => {
    const versions = selectedAltklausur?.versions || [];
    return (
      versions.find((version) => version.id === selectedAltVersionId) ||
      versions[0] ||
      null
    );
  }, [selectedAltVersionId, selectedAltklausur]);

  const selectedAltTasks = useMemo(
    () => (selectedAltVersion?.tasks || []).slice().sort(sortByPositionThenId),
    [selectedAltVersion]
  );

  const selectedAltQuizzes = useMemo(
    () => (selectedAltVersion?.quizQuestions || []).slice().sort(sortByPositionThenId),
    [selectedAltVersion]
  );

  const weeklyProfessorAltklausur = useMemo(
    () =>
      publishedAltklausuren.find(
        (alt) => String(alt?.id || "").toLowerCase() === WEEKLY_PROFESSOR_QUIZ_ALTKLAUSUR_ID
      ) || null,
    [publishedAltklausuren]
  );

  const weeklyProfessorVersion = useMemo(
    () => (weeklyProfessorAltklausur?.versions || [])[0] || null,
    [weeklyProfessorAltklausur]
  );

  const weeklyProfessorQuestionSlots = useMemo(
    () => orderWeeklyProfessorQuestions(weeklyProfessorVersion?.quizQuestions || []),
    [weeklyProfessorVersion]
  );

  useEffect(() => {
    setWeeklyQuizSession(null);
    setWeeklyQuizServerResults({});
    setWeeklyQuizCheckingId("");
    setWeeklyQuizErrors({});
  }, [weeklyProfessorVersion?.id]);

  const klausurPraxisAltklausuren = useMemo(() => {
    if (!hasKlausurPraxis) return [];

    return visibleAltklausuren
      .filter((alt) => String(alt?.id || "").toLowerCase() !== WEEKLY_PROFESSOR_QUIZ_ALTKLAUSUR_ID)
      .filter((alt) => {
        const version = alt?.versions?.[0] || null;
        return (version?.quizQuestions || []).length > 0 || (version?.tasks || []).length > 0;
      })
      .sort((a, b) => {
        if (a?.isTemporary && !b?.isTemporary) return -1;
        if (!a?.isTemporary && b?.isTemporary) return 1;
        const aDate = a?.versions?.[0]?.versionDate ? new Date(a.versions[0].versionDate).getTime() : 0;
        const bDate = b?.versions?.[0]?.versionDate ? new Date(b.versions[0].versionDate).getTime() : 0;
        if (aDate !== bDate) return bDate - aDate;
        return String(a?.title || "").localeCompare(String(b?.title || ""));
      });
  }, [hasKlausurPraxis, visibleAltklausuren]);


  useEffect(() => {
    if (activeTaskIndex < lessonTaskItems.length) return;
    setActiveTaskIndex(0);
  }, [activeTaskIndex, lessonTaskItems.length]);

  useEffect(() => {
    if (activeAltTaskIndex < selectedAltTasks.length) return;
    setActiveAltTaskIndex(0);
  }, [activeAltTaskIndex, selectedAltTasks.length]);

  useEffect(() => {
    if (activeAltQuizIndex < selectedAltQuizzes.length) return;
    setActiveAltQuizIndex(0);
  }, [activeAltQuizIndex, selectedAltQuizzes.length]);

  useEffect(() => {
    const cached = safeReadAiChatCache(aiChatStorageKey);
    setAiChatHistory(trimAiChatHistory(cached.history));
    setAiTaskMemory(cached.taskMemory);
    setAiChatInput("");
    setAiChatUploads([]);
    setAiChatError("");
    setAiChatLoading(false);
  }, [aiChatStorageKey]);

  useEffect(() => {
    if (!aiInfoOpen || !subjectId) return;
    const chats = aiChatDbData?.listSubjectAIChatsByUser || [];
    if (!Array.isArray(chats) || chats.length === 0) return;

    const storedChat = chats.find(
      (chat) => String(chat?.subjectID || "").toLowerCase() === String(subjectId).toLowerCase()
    );
    const storedHistory = trimAiChatHistory(storedChat?.conversation || []);
    if (storedHistory.length === 0) return;

    setAiChatHistory(storedHistory);
  }, [aiChatDbData, aiInfoOpen, subjectId]);

  useEffect(() => {
    if (!aiChatStorageKey) return;
    localStorage.setItem(
      aiChatStorageKey,
      JSON.stringify({
        history: trimAiChatHistory(aiChatHistory),
        taskMemory: aiTaskMemory || "",
      })
    );
  }, [aiChatHistory, aiChatStorageKey, aiTaskMemory]);

  useEffect(() => {
    if (!aiChatLoading) {
      setAiChatWaitIndex(0);
      return undefined;
    }
    const interval = setInterval(() => {
      setAiChatWaitIndex((prev) => Math.min(prev + 1, AI_CHAT_WAIT_STEPS.length - 1));
    }, 30000);
    return () => clearInterval(interval);
  }, [aiChatLoading]);

  useEffect(() => {
    if (!aiGenerating) {
      setAiGenerationWaitIndex(0);
      return undefined;
    }
    const interval = setInterval(() => {
      const steps = AI_GENERATION_WAIT_STEPS[aiGenerating] || AI_GENERATION_WAIT_STEPS.quiz;
      setAiGenerationWaitIndex((prev) => Math.min(prev + 1, steps.length - 1));
    }, 30000);
    return () => clearInterval(interval);
  }, [aiGenerating]);

  useEffect(() => {
    aiChatHistoryEndRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [aiChatHistory, aiChatLoading, aiChatUploads]);

  useEffect(() => {
    if (!showAltKlausurenComing && !showKlausurPraxis) {
      setSelectedAltId(null);
      setSelectedAltVersionId(null);
      return;
    }

    const activeAltklausuren = showKlausurPraxis
      ? klausurPraxisAltklausuren
      : visibleAltklausuren;

    if (activeAltklausuren.length === 0) {
      setSelectedAltId(null);
      setSelectedAltVersionId(null);
      return;
    }

    const nextAlt = activeAltklausuren.find((alt) => alt.id === selectedAltId) || null;
    if (selectedAltId && !nextAlt) {
      setSelectedAltId(null);
      setSelectedAltVersionId(null);
      return;
    }

    if (!nextAlt) {
      setSelectedAltVersionId(null);
      return;
    }

    const versions = nextAlt.versions || [];
    const nextVersion =
      versions.find((version) => version.id === selectedAltVersionId) ||
      versions[0] ||
      null;
    if ((nextVersion?.id || null) !== selectedAltVersionId) {
      setSelectedAltVersionId(nextVersion?.id || null);
    }
  }, [
    klausurPraxisAltklausuren,
    visibleAltklausuren,
    selectedAltId,
    selectedAltVersionId,
    showAltKlausurenComing,
    showKlausurPraxis,
  ]);

  useEffect(() => {
    setFeedbackRating(null);
    setFeedbackText("");
    setFeedbackError("");
    setShowFeedbackToast(false);
    setFeedbackClosedForLesson(false);
    setQuizOptionsById({});
    setPracticeTab(defaultPracticeTab);
    setActiveTaskIndex(0);
    setAltPracticeTab("aufgaben");
    setActiveAltTaskIndex(0);
    setActiveAltQuizIndex(0);
    setSelectedAltId(null);
    setSelectedAltVersionId(null);
    setWeeklyQuizSelections({});
    setWeeklyQuizEvaluated({});
    setWeeklyQuizSession(null);
    setWeeklyQuizServerResults({});
    setWeeklyQuizCheckingId("");
    setWeeklyQuizErrors({});
    setAiInfoOpen(false);
    setAiGenerating(null);
    setAiGenerationMessage("");
    setAiGenerationError("");
    setGeneratedQuizPreviews([]);
    setGeneratedTaskPreviews([]);
    setAiChatInput("");
    setAiChatUploads([]);
    setAiChatError("");
  }, [defaultPracticeTab, lessonId]);

  useEffect(() => {
    if (!lessonId) return;

    const alreadyCorrect = {};
    const selected = {};
    const attemptMap = {};
    const quizzesForState = effectiveQuizzes;

    if (attemptsData?.quizAttemptsByUser) {
      attemptsData.quizAttemptsByUser.forEach((a) => {
        attemptMap[a.quizId] = a;
      });
    }

    quizzesForState.forEach((q) => {
      const attempt = attemptMap[q.id];
      if (attempt?.isCorrect) {
        alreadyCorrect[q.id] = true;
        selected[q.id] = attempt.selectedOptionIds || [];
      }
    });

    setQuizState({
      selected,
      evaluate: false,
      alreadyCorrect,
    });

    const requiredQuizzes = quizzesForState.filter(isRequiredQuiz);
    if (requiredQuizzes.length > 0 && requiredQuizzes.every((q) => attemptMap[q.id]?.isCorrect)) {
      onLessonComplete(lessonId);
    }
  }, [
    attemptsData,
    effectiveQuizzes,
    lessonId,
    onLessonComplete,
  ]);

  const handleSelect = (quiz, optionId) => {
    setQuizState((prev) => ({
      ...prev,
      selected: {
        ...prev.selected,
        [quiz.id]:
          isMultipleQuizType(quiz.questionType)
            ? (prev.selected?.[quiz.id] || []).includes(optionId)
              ? (prev.selected?.[quiz.id] || []).filter((id) => id !== optionId)
              : [...(prev.selected?.[quiz.id] || []), optionId]
            : [optionId],
      },
    }));
  };

  const handleQuizOptionsLoaded = useCallback((quizId, options) => {
    setQuizOptionsById((prev) => {
      const current = prev[quizId] || [];
      if (
        current.length === options.length &&
        current.every((opt, index) => opt.id === options[index]?.id && opt.isCorrect === options[index]?.isCorrect)
      ) {
        return prev;
      }
      return { ...prev, [quizId]: options };
    });
  }, []);

  useEffect(() => {
    if (!lessonId) {
      setHiddenLessonContentIds([]);
      return;
    }
    setHiddenLessonContentIds(getDeletedLessonContentIds(lessonId));
  }, [lessonId]);

  const handleCheck = async () => {
    const quizzes = effectiveQuizzes;

    if (!quizState.evaluate) {
      if (userId) {
        await Promise.all(
          quizzes.map((q) => {
            const selectedOptionIds = quizState.selected?.[q.id] || [];
            if (selectedOptionIds.length === 0 || quizState.alreadyCorrect?.[q.id]) return null;
            return submitQuizAttempt({
              variables: {
                input: {
                  quizId: q.id,
                  userId,
                  selectedOptionIds,
                },
              },
            });
          })
        );
      }

      const newlyCorrect = {};
      quizzes.forEach((q) => {
        const selectedOptionIds = quizState.selected?.[q.id] || [];
        const correctIds = (quizOptionsById[q.id] || [])
          .filter((opt) => opt.isCorrect)
          .map((opt) => opt.id);
        if (selectedOptionIds.length === 0 || correctIds.length === 0) return;

        const selectedSet = new Set(selectedOptionIds);
        const allSelectedCorrect =
          selectedOptionIds.length === correctIds.length &&
          correctIds.every((id) => selectedSet.has(id));
        if (allSelectedCorrect) {
          newlyCorrect[q.id] = true;
        }
      });

      const updatedAlreadyCorrect = {
        ...quizState.alreadyCorrect,
        ...newlyCorrect,
      };
      const requiredQuizzes = quizzes.filter(isRequiredQuiz);
      const completionQuizzes = requiredQuizzes.length > 0 ? requiredQuizzes : quizzes;

      setQuizState((prev) => ({
        ...prev,
        evaluate: true,
        alreadyCorrect: updatedAlreadyCorrect,
      }));

      if (
        completionQuizzes.length > 0 &&
        completionQuizzes.every((q) => updatedAlreadyCorrect[q.id])
      ) {
        if (requiredQuizzes.length === 0 && userId) {
          try {
            await markLessonAsCompleted({
              variables: { input: { userId, lessonId } },
            });
          } catch (error) {
            console.warn("Could not mark lesson as completed", error);
          }
        }
        onLessonComplete(lessonId);
        if (userId) {
          await attemptsRefetch();
          await quizzesRefetch();
        }
      }
    } else {
      setQuizState((prev) => {
        const newSelected = { ...prev.selected };
        quizzes.forEach((q) => {
          if (!prev.alreadyCorrect[q.id]) {
            delete newSelected[q.id];
          }
        });
        return {
          ...prev,
          selected: newSelected,
          evaluate: false,
        };
      });
    }
  };

  // ✅ Feedback helpers
  const autoGrowTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const isAlreadyExistsError = (error) => {
    const message =
      error?.graphQLErrors?.[0]?.message || error?.message || "";
    const normalized = message.toLowerCase();
    return (
      normalized.includes("already") ||
      normalized.includes("exist") ||
      normalized.includes("duplicate")
    );
  };

  const ensureFeedbackAccess = async () => {
    if (!userId || !subjectId || !lessonId) return;

    try {
      await createEnrollment({
        variables: {
          input: {
            userId,
            subjectId,
          },
        },
      });
    } catch (error) {
      if (!isAlreadyExistsError(error)) {
        throw error;
      }
    }

    try {
      await createLessonEnrollment({
        variables: {
          input: {
            userId,
            subjectId,
            lessonId,
          },
        },
      });
    } catch (error) {
      if (!isAlreadyExistsError(error)) {
        throw error;
      }
    }
  };

  const takeFeedback = async () => {
    if (!lessonId) return;
    if (feedbackBusy) return;
    if (!feedbackRating) {
      setFeedbackError("Bitte wähle zuerst eine Bewertung aus.");
      return;
    }

    setFeedbackError("");
    setFeedbackBusy(true);
    try {
      await ensureFeedbackAccess();

      await submitLessonFeedback({
        variables: {
          input: {
            lessonId,
            rating: feedbackRating,
            comment: feedbackText.trim() || null,
          },
        },
      });

      setShowFeedbackToast(true);
      setFeedbackClosedForLesson(true);
      setFeedbackText("");
      setFeedbackRating(null);

      setTimeout(() => setShowFeedbackToast(false), 2600);

      setTimeout(() => {
        const el = textareaRef.current;
        if (el) el.style.height = "110px";
      }, 0);
    } catch (error) {
      const rawMessage =
        error?.graphQLErrors?.[0]?.message || error?.message || "";
      const lower = rawMessage.toLowerCase();
      const message = lower.includes("forbidden")
        ? "Feedback konnte nicht gespeichert werden (keine Berechtigung). Bitte öffne das Fach über dein Dashboard und versuch es erneut."
        : rawMessage ||
          "Feedback konnte nicht gesendet werden. Bitte versuch es erneut.";
      setFeedbackError(message);
    } finally {
      setFeedbackBusy(false);
    }
  };

  useEffect(() => {
    const root = contentRootRef.current;
    if (!root) return;

    return installSavedFormulaRenderer(root);
  }, [
    blocksData,
    contentData,
    effectiveQuizzes,
    generatedKlausurPreviews,
    generatedQuizPreviews,
    generatedTaskPreviews,
    aiChatHistory,
    lessonTasksData,
    lessonId,
    activeTaskIndex,
    selectedAltQuizzes,
    selectedAltTasks,
    activeAltTaskIndex,
    weeklyProfessorQuestionSlots,
    showAltKlausurenComing,
    showKlausurPraxis,
  ]);

  const hiddenContentIdSet = useMemo(
    () => new Set(hiddenLessonContentIds),
    [hiddenLessonContentIds]
  );
  const contents = useMemo(
    () =>
      (contentData?.lessonContentsByLesson || [])
        .filter((c) => !hiddenContentIdSet.has(c.id))
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [contentData, hiddenContentIdSet]
  );

  const canGenerateLessonQuiz =
    Boolean(lessonId && currentAiSubject?.generation === "quiz" && !isTutorOrAdmin);
  const canGenerateKlausurPraxis =
    Boolean(
      hasKlausurPraxis &&
        showKlausurPraxis &&
        userId &&
        currentAiSubject?.generation === "quiz" &&
        !isTutorOrAdmin
    );
  const canGenerateSharedKlausur = Boolean(
    showAltKlausurenComing &&
      sharedKlausurConfig &&
      userId &&
      currentAiSubject &&
      !isTutorOrAdmin
  );
  const canGenerateSimilarAufgabeForSubject =
    isSimilarAufgabenSubject(subjectId) && currentAiSubject?.generation === "aufgabe";
  const canGenerateSimilarAufgabe =
    Boolean(lessonId && canGenerateSimilarAufgabeForSubject && activeManualLessonTask);

  const buildCommonAiPayload = () => ({
    aiSubjectId: currentAiSubject?.id,
    subject_id: currentAiSubject?.id,
    subject: currentAiSubject?.title || subjData?.subject?.title || "",
    go_subject_id: subjectId || null,
    lesson_id: lessonId,
    lesson_title: lessonTitle || "",
    lesson_contents: toLessonContentPayload(contents),
  });

  const loadAllLessonTasksForKlausurGeneration = useCallback(async () => {
    const lessonItems = (lessons || []).filter((lesson) => lesson?.id);
    const results = await Promise.all(
      lessonItems.map((lesson) =>
        client
          .query({
            query: LESSON_TASKS_QUERY,
            variables: { lessonID: lesson.id },
            fetchPolicy: "network-only",
          })
          .then((result) => ({ lesson, tasks: result?.data?.lessonTasks || [] }))
          .catch(() => ({ lesson, tasks: [] }))
      )
    );

    return results;
  }, [client, lessons]);

  const scrollPracticeResultsIntoView = useCallback(() => {
    window.setTimeout(() => {
      scrollElementIntoViewSlowly(practiceResultsRef.current);
    }, 180);
  }, []);

  const getSourceTaskForGeneration = useCallback(() => {
    return activeManualLessonTask || null;
  }, [activeManualLessonTask]);

  const handleGenerateQuiz = async () => {
    if (!canGenerateLessonQuiz || aiGenerating) return;

    setAiGenerating("quiz");
    setAiGenerationWaitIndex(0);
    setAiGenerationError("");
    setAiGenerationMessage("");
    try {
      const result = await generateLessonQuiz({
        ...buildCommonAiPayload(),
        existing_quizzes: toExistingQuizPayload(effectiveQuizzes, quizOptionsById),
        desired_count: 4,
        difficulty: "course-appropriate",
        persist: true,
        // Saved quizzes are imported later through the normal database-backed
        // lesson query. Keep AI reuse off so first-time users generate their own
        // quiz set instead of cloning another user's generated rows.
        reuse_existing: false,
      });

      if (result.persisted) {
        setGeneratedQuizPreviews([]);
        setQuizOptionsById({});
        await quizzesRefetch();
        setAiGenerationMessage("Neue Quizfragen wurden gespeichert und werden künftig aus der Datenbank geladen.");
        scrollPracticeResultsIntoView();
      } else {
        const previews = (result.quizzes || []).map((quiz, index) =>
          normalizeGeneratedQuizPreview(quiz, index, lessonId)
        );
        setGeneratedQuizPreviews(previews);
        setAiGenerationMessage(
          previews.length > 0
            ? "KI-Quizfragen werden als temporäre Vorschau angezeigt."
            : "Die KI hat keine neuen Quizfragen zurückgegeben."
        );
        if (previews.length > 0) scrollPracticeResultsIntoView();
      }
    } catch (error) {
      setAiGenerationError(error?.message || "Quizfragen konnten nicht generiert werden.");
    } finally {
      setAiGenerating(null);
    }
  };

  const handleGenerateKlausurPraxis = async () => {
    if (!canGenerateKlausurPraxis || aiGenerating) return;

    setAiGenerating("klausur");
    setAiGenerationWaitIndex(0);
    setAiGenerationError("");
    setAiGenerationMessage("");

    try {
      const result = await generateKlausurPraxis({
        aiSubjectId: currentAiSubject.id,
        subject_id: currentAiSubject.id,
        subject: currentAiSubject.title,
        go_subject_id: subjectId,
        user_id: userId,
        created_by: userId,
        question_count: KLAUSUR_PRAXIS_QUESTION_COUNT,
        difficulty_mode: "random",
        question_type: "single_choice",
        option_count_min: KLAUSUR_PRAXIS_MIN_OPTIONS,
        option_count_max: KLAUSUR_PRAXIS_MAX_OPTIONS,
        persist: true,
        storage_target: "altklausur",
        visibility: "shared_subject",
        share_with_subject_students: true,
        generated_for_user_id: userId,
      });

      if (result?.persisted) {
        const preview = await prepareGeneratedKlausurPreview(result, subjectId, userId);
        if (getKlausurPrimaryItemCount(preview) > 0) {
          setGeneratedKlausurPreviews((prev) => [
            preview,
            ...prev.filter((item) => item.id !== preview.id),
          ]);
        }
        await altRefetch?.();
        setAiGenerationMessage("Die Klausur wurde gespeichert und ist für alle eingeschriebenen Studierenden sichtbar.");
        return;
      }

      const preview = await prepareGeneratedKlausurPreview(result, subjectId, userId);
      if (getKlausurPrimaryItemCount(preview) > 0) {
        setGeneratedKlausurPreviews((prev) => [preview, ...prev]);
        setAiGenerationMessage(
          "Die Klausur wird als temporäre Vorschau angezeigt, bis die Backend-Speicherung aktiv ist."
        );
      } else {
        setAiGenerationMessage("Die KI hat keine Klausurinhalte zurückgegeben.");
      }
    } catch (error) {
      setAiGenerationError(error?.message || "Klausur konnte nicht generiert werden.");
    } finally {
      setAiGenerating(null);
    }
  };

  const handleGenerateSharedKlausur = async () => {
    if (!canGenerateSharedKlausur || aiGenerating || !sharedKlausurConfig) return;

    const startedAt = Date.now();
    writeSharedKlausurPending(sharedKlausurStorageKey, {
      status: "pending",
      subjectId,
      userId,
      startedAt,
      label: sharedKlausurConfig.label,
    });

    setAiGenerating("sharedKlausur");
    setAiGenerationWaitIndex(0);
    setAiGenerationError("");
    setAiGenerationMessage("");

    try {
      const lessonTaskGroups = await loadAllLessonTasksForKlausurGeneration();
      const lessonTaskContext = toLessonTaskContextPayload(lessonTaskGroups);
      const flatManualLessonTasks = lessonTaskContext.flatMap((lesson) =>
        (lesson.tasks || []).map((task) => ({
          ...task,
          lesson_id: lesson.lesson_id,
          lesson_title: lesson.lesson_title,
        }))
      );
      const existingAltklausuren = toExistingAltklausurPayload(publishedAltklausuren);
      const existingAltklausurTasks = existingAltklausuren.flatMap((alt) =>
        (alt.versions || []).flatMap((version) =>
          (version.tasks || []).map((task) => ({
            ...task,
            altklausur_id: alt.id,
            altklausur_title: alt.title,
            altklausur_version_id: version.id,
            altklausur_version_title: version.title,
          }))
        )
      );

      const result = await generateKlausurPraxis({
        ...buildCommonAiPayload(),
        generation_mode: sharedKlausurConfig.mode,
        user_id: userId,
        created_by: userId,
        requester_user_name: localUserName,
        generated_for_user_id: userId,
        visibility: "shared_subject",
        share_with_subject_students: true,
        persist: true,
        storage_target: "altklausur",
        response_format: "altklausur_tasks_html_katex",
        expected_duration_minutes: sharedKlausurConfig.expected_duration_minutes,
        exercise_count: sharedKlausurConfig.exercise_count || null,
        exercise_count_min: sharedKlausurConfig.exercise_count_min || null,
        exercise_count_max: sharedKlausurConfig.exercise_count_max || null,
        big_exercise_count: sharedKlausurConfig.big_exercise_count || null,
        small_exercise_count: sharedKlausurConfig.small_exercise_count || null,
        exercise_count_policy: sharedKlausurConfig.exercise_count_policy,
        exercise_mix: sharedKlausurConfig.exercise_mix,
        include_tasks: true,
        include_quiz_questions: false,
        include_solutions: true,
        allow_task_images: true,
        allow_solution_images: true,
        image_upload_strategy: "frontend_cloudinary_or_backend_asset",
        include_hidden_image_metadata: true,
        reuse_existing: false,
        generation_limit: {
          per_user_per_subject: 1,
          stored_on: "Enrollment.generatedAltklausurenCount / Altklausur.generatedForUserId",
        },
        requested_storage: {
          altklausur: true,
          source_type: "ai",
          is_published: true,
          generated_for_user_id: userId,
          created_by: userId,
          shared_visibility: true,
        },
        existing_lessons: lessonTaskContext,
        existing_tasks: flatManualLessonTasks,
        existing_altklausuren: existingAltklausuren,
        existing_altklausur_tasks: existingAltklausurTasks,
      });

      const preview = await prepareGeneratedKlausurPreview(result, subjectId, userId);
      if (getKlausurPrimaryItemCount(preview) > 0) {
        setGeneratedKlausurPreviews((prev) => [
          preview,
          ...prev.filter((item) => item.id !== preview.id),
        ]);
      }

      if (result?.persisted) {
        await altRefetch?.();
        setAiGenerationMessage("Die neue Klausur wurde gespeichert und ist für alle eingeschriebenen Studierenden sichtbar.");
      } else if (getKlausurPrimaryItemCount(preview) > 0) {
        setAiGenerationMessage("Die neue Klausur wird als temporäre Vorschau angezeigt, bis die Backend-Speicherung aktiv ist.");
      } else {
        setAiGenerationMessage("LearnHut AI hat noch keine Klausuraufgaben zurückgegeben.");
      }
      clearSharedKlausurPending(sharedKlausurStorageKey, "completed");
    } catch (error) {
      setAiGenerationError(error?.message || "Klausur konnte nicht generiert werden.");
      clearSharedKlausurPending(sharedKlausurStorageKey, "failed");
    } finally {
      setAiGenerating(null);
    }
  };

  const handleGenerateSimilarAufgabe = async () => {
    if (aiGenerating) return;

    setAiGenerationWaitIndex(0);
    setAiGenerationError("");
    setAiGenerationMessage("");

    if (!canGenerateSimilarAufgabeForSubject) {
      setAiGenerationError(AI_SIMILAR_AUFGABEN_LOCK_TITLE);
      return;
    }

    if (!activeManualLessonTask) {
      setAiGenerationError(AI_SIMILAR_AUFGABEN_MANUAL_ONLY_TITLE);
      return;
    }

    const sourceTask = getSourceTaskForGeneration();
    if (!sourceTask) {
      setAiGenerationError("Für diese Lektion gibt es noch keine Aufgabe, aus der LearnHut AI eine ähnliche Aufgabe erstellen kann.");
      return;
    }

    const sourceTaskPayload = toExistingTaskPayload([sourceTask])[0];
    if (!String(sourceTaskPayload?.task_text || "").trim()) {
      setAiGenerationError("Die vorhandene Aufgabe ist leer. Bitte öffne eine Aufgabe mit Aufgabentext.");
      return;
    }

    setAiGenerating("aufgabe");
    try {
      const sourceTaskIndex = lessonTaskItems.findIndex(
        (task) => getTaskId(task) === getTaskId(sourceTask)
      );
      const existingTasksPayload = toExistingTaskPayload(lessonTaskItems);
      const result = await generateSimilarAufgabe({
        ...buildCommonAiPayload(),
        user_id: userId,
        current_task: sourceTaskPayload,
        existing_tasks: existingTasksPayload,
        difficulty: sourceTask?.difficulty || "course-appropriate",
        persist: true,
        reuse_existing: false,
        response_format: "html_katex",
        image_upload_strategy: "frontend_cloudinary",
        requested_storage: {
          lesson_tasks: true,
          origin_task_id: sourceTask.id,
          generated_for_user_id: userId,
        },
      });

      if (result?.persisted) {
        setGeneratedTaskPreviews([]);
        const refreshed = await lessonTasksRefetch?.();
        const refreshedTasks = refreshed?.data?.lessonTasks || [];
        const refreshedSourceIndex = refreshedTasks.findIndex(
          (task) => getTaskId(task) === getTaskId(sourceTask)
        );
        setActiveTaskIndex(
          refreshedSourceIndex >= 0 ? refreshedSourceIndex : Math.max(0, sourceTaskIndex)
        );
        setAiGenerationMessage("Neue ähnliche Aufgabe wurde gespeichert und ist nur für dich sichtbar.");
        scrollPracticeResultsIntoView();
        return;
      }

      const generatedTask = result?.task;
      if (!generatedTask) {
        setAiGenerationMessage("LearnHut AI hat keine neue Aufgabe zurückgegeben.");
        return;
      }

      const preview = await prepareGeneratedTaskPreview(
        generatedTask,
        result,
        generatedTaskPreviews.length,
        lessonId,
        sourceTask
      );

      if (shouldFinalizeGeneratedTaskAfterImageUpload(result)) {
        const persistedResult = await persistGeneratedAufgabe({
          ...buildCommonAiPayload(),
          user_id: userId,
          task: toGeneratedTaskPersistPayload(preview, sourceTask, lessonId, userId),
          origin_task_id: sourceTask.id,
          generated_for_user_id: userId,
        });

        if (persistedResult?.persisted) {
          const refreshed = await lessonTasksRefetch?.();
          const refreshedTasks = refreshed?.data?.lessonTasks || [];
          const savedTaskId = persistedResult.task?.id || "";
          const refetchIncludesTask =
            savedTaskId && refreshedTasks.some((task) => task?.id === savedTaskId);

          if (!refetchIncludesTask && persistedResult.task) {
            const fallbackPreview = normalizeGeneratedTaskPreview(
              persistedResult.task,
              generatedTaskPreviews.length,
              lessonId,
              sourceTask
            );
            setGeneratedTaskPreviews((prev) => [
              ...prev.filter((task) => task.id !== fallbackPreview.id),
              fallbackPreview,
            ]);
            setActiveTaskIndex(Math.max(0, sourceTaskIndex));
          } else {
            setGeneratedTaskPreviews([]);
            const refreshedSourceIndex = refreshedTasks.findIndex(
              (task) => getTaskId(task) === getTaskId(sourceTask)
            );
            setActiveTaskIndex(
              refreshedSourceIndex >= 0 ? refreshedSourceIndex : Math.max(0, sourceTaskIndex)
            );
          }
          setAiGenerationMessage("Neue ähnliche Aufgabe wurde mit Skizze gespeichert und ist nur für dich sichtbar.");
          scrollPracticeResultsIntoView();
          return;
        }
      }

      setGeneratedTaskPreviews((prev) => [...prev, preview]);
      setActiveTaskIndex(Math.max(0, sourceTaskIndex));
      setAiGenerationMessage("Die ähnliche Aufgabe wird als temporäre Vorschau angezeigt, bis die Backend-Speicherung aktiv ist.");
      scrollPracticeResultsIntoView();
    } catch (error) {
      setAiGenerationError(error?.message || "Ähnliche Aufgabe konnte nicht generiert werden.");
    } finally {
      setAiGenerating(null);
    }
  };

  const addAiChatUploads = useCallback((files) => {
    const incoming = Array.from(files || []);
    if (incoming.length === 0) return;

    const accepted = incoming.filter(isAcceptedAiUpload);
    setAiChatUploads((prev) => mergeAiChatUploads(prev, accepted));
    if (accepted.length === 0) {
      setAiChatError("Dieses Dateiformat wird im Chat noch nicht unterstützt.");
    } else {
      setAiChatError("");
    }
  }, []);

  const handleAiChatUploadChange = (event) => {
    addAiChatUploads(event.target.files || []);
    event.target.value = "";
  };

  const dragHasFiles = (event) =>
    Array.from(event.dataTransfer?.types || []).includes("Files");

  const handleAiChatDragEnter = (event) => {
    if (!dragHasFiles(event)) return;
    event.preventDefault();
    if (!canUseSubjectChat || aiChatLoading) return;
    setAiChatDropActive(true);
  };

  const handleAiChatDragOver = (event) => {
    if (!dragHasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = aiChatLoading ? "none" : "copy";
    if (!canUseSubjectChat || aiChatLoading) return;
    setAiChatDropActive(true);
  };

  const handleAiChatDragLeave = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setAiChatDropActive(false);
  };

  const handleAiChatDrop = async (event) => {
    if (!dragHasFiles(event)) return;
    event.preventDefault();
    setAiChatDropActive(false);
    if (!canUseSubjectChat || aiChatLoading) return;
    const files = await filesFromDataTransfer(event.dataTransfer);
    addAiChatUploads(files);
  };

  const removeAiChatUpload = (indexToRemove) => {
    setAiChatUploads((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubjectChatSubmit = async (event) => {
    event?.preventDefault?.();
    const typedMessage = aiChatInput.trim();
    const uploadsForRequest = aiChatUploads;
    const message =
      typedMessage ||
      (uploadsForRequest.length > 0
        ? "Bitte analysiere die hochgeladenen Unterlagen und gib mir zuerst einen hilfreichen Hinweis."
        : "");
    if (!message || aiChatLoading || !canUseSubjectChat) return;

    const uploadNames = uploadsForRequest.map((file) => file.name);
    const nextHistory = trimAiChatHistory([
      ...aiChatHistory,
      { role: "user", content: message, attachments: uploadNames },
    ]);
    setAiChatHistory(nextHistory);
    setAiChatInput("");
    setAiChatUploads([]);
    setAiChatError("");
    setAiChatLoading(true);
    setAiChatWaitIndex(0);

    try {
      const response = await sendSubjectChat({
        aiSubjectId: currentAiSubject.id,
        subject_id: currentAiSubject.id,
        subject: currentAiSubject.title,
        go_subject_id: subjectId || null,
        lesson_id: lessonId || null,
        message,
        history: trimAiChatHistory(aiChatHistory).map(({ role, content }) => ({ role, content })),
        uploads: uploadsForRequest,
        task_memory: aiTaskMemory,
        persist_chat: AI_PERSIST_CHAT,
      });

      const answer = getChatAnswer(response);
      setAiTaskMemory(response?.task_memory || aiTaskMemory);
      setAiChatHistory(trimAiChatHistory([
        ...nextHistory,
        {
          role: "assistant",
          content: answer || "Ich habe keine Antwort erhalten.",
          html: response?.answer_html || "",
          attachments: response?.attachments || [],
          sources: response?.sources || [],
        },
      ]));
    } catch (error) {
      setAiChatError(error?.message || "KI-Assistent konnte nicht antworten.");
      setAiChatHistory(nextHistory);
      setAiChatUploads(uploadsForRequest);
    } finally {
      setAiChatLoading(false);
    }
  };

  const nextLesson = useMemo(() => {
    if (!lessonId) return null;

    const orderedCategories = (lessonCategories || [])
      .slice()
      .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
      .map((cat) => ({
        ...cat,
        lessons: (cat?.lessons || [])
          .filter((l) => l?.isPublic !== false)
          .slice()
          .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0)),
      }))
      .filter((cat) => cat.lessons.length > 0);

    let currentCategoryIndex = -1;
    let currentLessonIndex = -1;

    for (let i = 0; i < orderedCategories.length; i += 1) {
      const lessonIndex = orderedCategories[i].lessons.findIndex(
        (l) => l.id === lessonId
      );
      if (lessonIndex !== -1) {
        currentCategoryIndex = i;
        currentLessonIndex = lessonIndex;
        break;
      }
    }

    if (currentCategoryIndex === -1) {
      const currentFlatIndex = (lessons || []).findIndex((l) => l.id === lessonId);
      if (currentFlatIndex === -1) return null;
      return (
        (lessons || [])
          .slice(currentFlatIndex + 1)
          .find((l) => l?.isPublic !== false) || null
      );
    }

    const currentCategory = orderedCategories[currentCategoryIndex];
    const nextInSameCategory = currentCategory.lessons[currentLessonIndex + 1] || null;
    if (nextInSameCategory) return nextInSameCategory;

    for (let i = currentCategoryIndex + 1; i < orderedCategories.length; i += 1) {
      if (orderedCategories[i].lessons.length > 0) {
        return orderedCategories[i].lessons[0];
      }
    }

    return null;
  }, [lessonCategories, lessonId, lessons]);

  const firstPublicLessonInFirstCategory = useMemo(() => {
    const orderedCategories = (lessonCategories || [])
      .slice()
      .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
      .map((cat) => ({
        ...cat,
        lessons: (cat?.lessons || [])
          .filter((l) => l?.isPublic !== false)
          .slice()
          .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0)),
      }));

    if (orderedCategories.length === 0) return null;
    return orderedCategories[0]?.lessons?.[0] || null;
  }, [lessonCategories]);

  const handleWeeklyQuizOptionSelect = useCallback(
    (question, optionId) => {
      const questionId = question?.id;
      if (!questionId || weeklyQuizEvaluated[questionId]) return;

      setWeeklyQuizSelections((prev) => {
        const current = prev[questionId] || [];
        const isMultiple = isMultipleQuizType(question?.questionType);
        const next = isMultiple
          ? current.includes(optionId)
            ? current.filter((id) => id !== optionId)
            : [...current, optionId]
          : [optionId];

        return { ...prev, [questionId]: next };
      });
    },
    [weeklyQuizEvaluated]
  );

  const ensureWeeklyQuizSession = useCallback(async () => {
    if (!weeklyProfessorVersion?.id) {
      throw new Error("Wochenquiz-Sitzung konnte nicht gestartet werden.");
    }

    if (
      weeklyQuizSession?.id &&
      String(weeklyQuizSession.altklausurVersionID || "") === String(weeklyProfessorVersion.id) &&
      weeklyQuizSession.status === "in_progress"
    ) {
      return weeklyQuizSession;
    }

    const { data } = await startWeeklyQuizSession({
      variables: {
        input: {
          altklausurVersionID: weeklyProfessorVersion.id,
          mode: "guided",
        },
      },
    });
    const session = data?.startAltklausurQuizSession;
    if (!session?.id) {
      throw new Error("Wochenquiz-Sitzung konnte nicht gestartet werden.");
    }
    setWeeklyQuizSession(session);
    return session;
  }, [startWeeklyQuizSession, weeklyProfessorVersion?.id, weeklyQuizSession]);

  const handleWeeklyQuizEvaluate = useCallback(
    async (question) => {
      const questionId = question?.id;
      const selectedOptionIds = weeklyQuizSelections[questionId] || [];
      if (!questionId || selectedOptionIds.length === 0 || weeklyQuizCheckingId) return;

      const localCorrectIds = (question?.options || [])
        .filter(isCorrectOption)
        .map((option) => String(option.id || ""))
        .filter(Boolean);

      if (localCorrectIds.length > 0) {
        setWeeklyQuizErrors((prev) => ({ ...prev, [questionId]: "" }));
        setWeeklyQuizEvaluated((prev) => ({ ...prev, [questionId]: true }));
        return;
      }

      setWeeklyQuizCheckingId(questionId);
      setWeeklyQuizErrors((prev) => ({ ...prev, [questionId]: "" }));
      try {
        const session = await ensureWeeklyQuizSession();
        const sessionQuestion = (session.sessionQuestions || []).find(
          (item) => String(item?.questionID || "") === String(questionId)
        );
        if (!sessionQuestion?.id) {
          throw new Error("Quizfrage wurde in der Wochenquiz-Sitzung nicht gefunden.");
        }

        const { data } = await saveWeeklyQuizSessionAnswer({
          variables: {
            input: {
              sessionQuestionID: sessionQuestion.id,
              optionIDs: selectedOptionIds,
            },
          },
        });
        const updated = data?.saveAltklausurQuizSessionAnswer;
        if (!updated?.id) {
          throw new Error("Antwort konnte nicht geprüft werden.");
        }

        setWeeklyQuizSession((prev) => {
          const base = prev?.id ? prev : session;
          return {
            ...base,
            sessionQuestions: (base.sessionQuestions || []).map((item) =>
              item.id === updated.id ? { ...item, ...updated } : item
            ),
          };
        });
        setWeeklyQuizServerResults((prev) => ({
          ...prev,
          [questionId]: {
            isCorrect: Boolean(updated.isCorrect),
            selectedOptionIds: toOptionIds(updated.selectedOptionIds).length
              ? toOptionIds(updated.selectedOptionIds)
              : toOptionIds(selectedOptionIds),
          },
        }));
        setWeeklyQuizEvaluated((prev) => ({ ...prev, [questionId]: true }));
      } catch (error) {
        setWeeklyQuizErrors((prev) => ({
          ...prev,
          [questionId]: error?.message || "Antwort konnte nicht geprüft werden.",
        }));
      } finally {
        setWeeklyQuizCheckingId("");
      }
    },
    [
      ensureWeeklyQuizSession,
      saveWeeklyQuizSessionAnswer,
      weeklyQuizCheckingId,
      weeklyQuizSelections,
    ]
  );

  const handleWeeklyQuizReset = useCallback((questionId) => {
    if (!questionId) return;
    setWeeklyQuizSelections((prev) => ({ ...prev, [questionId]: [] }));
    setWeeklyQuizEvaluated((prev) => ({ ...prev, [questionId]: false }));
    setWeeklyQuizServerResults((prev) => ({ ...prev, [questionId]: null }));
    setWeeklyQuizErrors((prev) => ({ ...prev, [questionId]: "" }));
  }, []);

  const renderWeeklyProfessorQuizCard = ({ slot, question }) => {
    if (!question) {
      return (
        <article className="weekly-quiz-card weekly-quiz-card-missing" key={slot.id}>
          <div className="weekly-quiz-card-head">
            <span>{slot.title}</span>
            <span>Noch nicht verfügbar</span>
          </div>
          <h3>{slot.tutorTitle}</h3>
          <p>Diese geprüfte Quizfrage wurde noch nicht veröffentlicht.</p>
        </article>
      );
    }

    const options = (question.options || []).slice().sort(sortByPositionThenId);
    const isMultiple = isMultipleQuizType(question.questionType);
    const selectedIds = weeklyQuizSelections[question.id] || [];
    const isEvaluated = !!weeklyQuizEvaluated[question.id];
    const serverResult = weeklyQuizServerResults[question.id] || null;
    const checkingThisQuestion = weeklyQuizCheckingId === question.id;
    const checkError = weeklyQuizErrors[question.id] || "";
    const correctIds = options
      .filter(isCorrectOption)
      .map((option) => String(option.id || ""))
      .filter(Boolean);
    const hasLocalAnswerKey = correctIds.length > 0;
    const hasSelection = selectedIds.length > 0;
    const isCorrect =
      isEvaluated &&
      (hasLocalAnswerKey
        ? selectedIds.length === correctIds.length &&
          selectedIds.every((id) => correctIds.includes(String(id || "")))
        : Boolean(serverResult?.isCorrect));

    return (
      <article className="weekly-quiz-card" key={question.id}>
        <div className="weekly-quiz-card-head">
          <div>
            <span className="weekly-quiz-kicker">{slot.badge}</span>
            <h3>{slot.title}</h3>
          </div>
          <span className="weekly-quiz-verified">Vom Prof. geprüft</span>
        </div>

        <div className="weekly-quiz-question-band">
          <span>{isMultiple ? "Mehrfachauswahl" : "Single Choice"}</span>
          <span>{Math.max(1, Number(question.points || 1))} Punkte</span>
        </div>

        <div className="weekly-quiz-question-body">
          <div
            className="weekly-quiz-question-rich lh-lessonContent"
            dangerouslySetInnerHTML={{
              __html: normalizeRichHtmlForDisplay(question.questionText, "Unbenannte Quizfrage"),
            }}
          />
          {question.hint && !isEvaluated ? <p>{question.hint}</p> : null}
        </div>

        <div className="weekly-quiz-options" role="group" aria-label={slot.title}>
          {options.map((option) => {
            const isSelected = selectedIds.includes(option.id);
            const optionState = isEvaluated
              ? hasLocalAnswerKey
                ? isCorrectOption(option)
                  ? "is-correct"
                  : isSelected
                  ? "is-incorrect"
                  : ""
                : isSelected
                ? serverResult?.isCorrect
                  ? "is-correct"
                  : "is-incorrect"
                : ""
              : isSelected
              ? "is-selected"
              : "";

            return (
              <label className={`weekly-quiz-option ${optionState}`} key={option.id}>
                <input
                  type={isMultiple ? "checkbox" : "radio"}
                  name={`weekly-professor-quiz-${question.id}`}
                  checked={isSelected}
                  disabled={isEvaluated}
                  onChange={() => handleWeeklyQuizOptionSelect(question, option.id)}
                />
                <span>{option.optionText}</span>
              </label>
            );
          })}
        </div>

        {checkError && !isEvaluated ? (
          <div className="weekly-quiz-feedback is-incorrect">
            <strong>Antwort konnte nicht geprüft werden</strong>
            <p>{checkError}</p>
          </div>
        ) : null}

        {isEvaluated ? (
          <div className={`weekly-quiz-feedback ${isCorrect ? "is-correct" : "is-incorrect"}`}>
            <strong>{isCorrect ? "Richtig beantwortet" : "Noch nicht ganz richtig"}</strong>
            {question.explanation ? <p>{question.explanation}</p> : null}
          </div>
        ) : null}

        <div className="weekly-quiz-actions">
          {isEvaluated && !isCorrect ? (
            <button type="button" onClick={() => handleWeeklyQuizReset(question.id)}>
              Noch einmal versuchen
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleWeeklyQuizEvaluate(question)}
              disabled={!hasSelection || isCorrect || checkingThisQuestion}
            >
              {checkingThisQuestion ? "Wird geprüft..." : isCorrect ? "Antwort geprüft" : "Antworten prüfen"}
            </button>
          )}
        </div>
      </article>
    );
  };

  const getAltklausurCreatorLabel = (alt) => {
    if (!isAiGeneratedAltklausur(alt)) return "";
    const creatorId = getAltklausurCreatorId(alt);
    return (
      alt?.createdByName ||
      alt?.created_by_name ||
      (creatorId ? altCreatorNames[creatorId] : "") ||
      (String(creatorId || "") === String(userId || "") ? localUserName : "") ||
      "LearnHut Student"
    );
  };

  const renderGeneratedByLabel = (alt) => {
    const creatorLabel = getAltklausurCreatorLabel(alt);
    return creatorLabel ? <span>Generiert von {creatorLabel}</span> : null;
  };

  const renderSharedKlausurGenerationStatus = () => {
    const isSharedGenerating = aiGenerating === "sharedKlausur";
    if (!isSharedGenerating && !sharedKlausurPending && !aiGenerationMessage && !aiGenerationError) {
      return null;
    }

    return (
      <div className={`ai-generation-status shared-klausur-status ${aiGenerationError ? "is-error" : ""}`}>
        {isSharedGenerating || sharedKlausurPending ? (
          <span className="ai-waiting-inline">
            {isSharedGenerating
              ? aiGenerationStepLabel
              : "Eine Klausur-Generierung läuft im Hintergrund. Das kann ca. 7-8 Minuten dauern."}
            <span className="ai-waiting-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </span>
        ) : (
          aiGenerationError || aiGenerationMessage
        )}
        {(isSharedGenerating || sharedKlausurPending) && (
          <p>
            Du kannst währenddessen weiterlernen. Wenn die Backend-Speicherung aktiv ist,
            erscheint die Klausur nach Abschluss automatisch in dieser Übersicht.
          </p>
        )}
      </div>
    );
  };

  const renderKlausurPraxis = () => {
    const selectedKlausur =
      klausurPraxisAltklausuren.find((alt) => alt.id === selectedAltId) || null;
    const selectedVersion = selectedKlausur?.versions?.[0] || null;
    const selectedQuestions = (selectedVersion?.quizQuestions || [])
      .slice()
      .sort(sortByPositionThenId);
    const selectedTasks = (selectedVersion?.tasks || [])
      .slice()
      .sort(sortByPositionThenId);
    const formatDate = (value) => {
      if (!value) return "Heute";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "Heute";
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };

    if (selectedKlausur) {
      return (
        <section className="klausur-praxis" aria-live="polite">
          <button
            type="button"
            className="alt-klausuren-back-btn"
            onClick={() => {
              setSelectedAltId(null);
              setSelectedAltVersionId(null);
              setActiveAltTaskIndex(0);
              setActiveAltQuizIndex(0);
            }}
          >
            Zurück zur Klausurenliste
          </button>

          <div className="klausur-praxis-head">
            <div>
              <span>Klausur Praxis</span>
              <h2>{selectedKlausur.title || "Generierte Klausur"}</h2>
              {selectedKlausur.description ? <p>{selectedKlausur.description}</p> : null}
            </div>
            <div className="alt-klausuren-metrics">
              {selectedTasks.length > 0 ? <span>{selectedTasks.length} Aufgaben</span> : null}
              {selectedQuestions.length > 0 ? <span>{selectedQuestions.length} Quizfragen</span> : null}
              {selectedQuestions.length > 0 ? <span>Single Choice</span> : null}
              {selectedKlausur.isTemporary ? <span>Temporär</span> : null}
            </div>
          </div>

          {selectedTasks.length > 0 || selectedQuestions.length > 0 ? (
            <div className="lesson-practice-section klausur-praxis-panel">
              {selectedTasks.length > 0 ? (
                <LessonTaskCarousel
                  tasks={selectedTasks}
                  activeIndex={activeAltTaskIndex}
                  onActiveIndexChange={setActiveAltTaskIndex}
                />
              ) : (
                <AltklausurQuizCarousel
                  questions={selectedQuestions}
                  activeIndex={activeAltQuizIndex}
                  onActiveIndexChange={setActiveAltQuizIndex}
                  compactSidePreviews={hasKlausurPraxis}
                  versionId={selectedVersion?.id || ""}
                />
              )}
            </div>
          ) : (
            <div className="practice-empty-state">
              <div className="practice-empty-title">Keine Inhalte gefunden</div>
              <div className="practice-empty-copy">
                Diese Klausur enthält noch keine gespeicherten Aufgaben oder Quizfragen.
              </div>
            </div>
          )}
        </section>
      );
    }

    return (
      <section className="klausur-praxis" aria-live="polite">
        <div className="klausur-praxis-head">
          <div>
            <span>Gemeinsamer Praxismodus</span>
            <h2>Klausur Praxis</h2>
            <p>
              Generiere eine Klausur, die nach dem Speichern allen eingeschriebenen Studierenden angezeigt wird.
            </p>
          </div>
          <button
            type="button"
            className="klausur-praxis-generate-btn"
            onClick={handleGenerateKlausurPraxis}
            disabled={!canGenerateKlausurPraxis || aiGenerating === "klausur"}
            title={
              canGenerateKlausurPraxis
                ? "Neue Klausur mit LearnHut AI generieren"
                : "Klausur Praxis ist für dieses Fach noch nicht bereit."
            }
          >
            {aiGenerating === "klausur" ? "Klausur wird generiert..." : "Neue Klausur generieren"}
          </button>
        </div>

        <div className="klausur-praxis-specs" aria-label="Klausur Kriterien">
          <span>20 Quizfragen</span>
          <span>Single Choice</span>
          <span>3-5 Antwortoptionen</span>
          <span>Zufällige Schwierigkeit</span>
        </div>

        {(aiGenerating === "klausur" || aiGenerationMessage || aiGenerationError) && (
          <div className={`ai-generation-status ${aiGenerationError ? "is-error" : ""}`}>
            {aiGenerating === "klausur" ? (
              <span className="ai-waiting-inline">
                {aiGenerationStepLabel}
                <span className="ai-waiting-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </span>
            ) : (
              aiGenerationError || aiGenerationMessage
            )}
          </div>
        )}

        {altLoading ? (
          <div className="practice-empty-state">
            <div className="practice-empty-title">Klausuren werden geladen...</div>
          </div>
        ) : altError ? (
          <div className="practice-empty-state">
            <div className="practice-empty-title">Klausuren konnten nicht geladen werden</div>
            <div className="practice-empty-copy">Bitte versuch es später erneut.</div>
          </div>
        ) : klausurPraxisAltklausuren.length === 0 ? (
          <div className="klausur-praxis-empty">
            <span>Bereit zum Start</span>
            <h2>Noch keine Klausur</h2>
            <p>
              Klicke auf „Neue Klausur generieren“, um den ersten Praxissatz für dieses Fach zu erstellen.
            </p>
          </div>
        ) : (
          <div className="klausur-praxis-card-list">
            {klausurPraxisAltklausuren.map((alt, index) => {
              const version = alt.versions?.[0] || null;
              const questionCount = (version?.quizQuestions || []).length;
              const taskCount = (version?.tasks || []).length;
              return (
                <article className="klausur-praxis-card" key={alt.id}>
                  <div className="klausur-praxis-card-main">
                    <div className="klausur-praxis-card-topline">
                      {index === 0 ? <span>NEW</span> : null}
                      <small>{formatDate(version?.versionDate)}</small>
                    </div>
                    <h2>{alt.title || "Generierte Klausur"}</h2>
                    <div className="klausur-praxis-card-meta">
                      {taskCount > 0 ? <span>{taskCount} Aufgaben</span> : null}
                      {questionCount > 0 ? <span>{questionCount} Fragen</span> : null}
                      {questionCount > 0 ? <span>Single Choice</span> : null}
                      {isAiGeneratedAltklausur(alt) ? renderGeneratedByLabel(alt) : null}
                      {alt.isTemporary ? <span>Temporär</span> : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="klausur-praxis-start-btn"
                    onClick={() => {
                      setSelectedAltId(alt.id);
                      setSelectedAltVersionId(version?.id || null);
                      setActiveAltTaskIndex(0);
                      setActiveAltQuizIndex(0);
                    }}
                  >
                    Klausur Starten
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  // ---------------- RENDER: Subject Home (no lesson selected) ----------------
  if (!lessonId) {
    if (subjLoading || blocksLoading)
      return <div className="subject-right-container">Loading…</div>;
    if (subjError || blocksError)
      return (
        <div className="subject-right-container">Error loading subject.</div>
      );

    const blocks = (blocksData?.subjectDetailBlocks || [])
      .slice()
      .filter((b) => b?.id)
      .sort(sortByPositionThenId);

    return (
      <div
        className="subject-right-container"
        ref={contentRootRef}
      >
        <h1 className="subject-title">
          {showKlausurPraxis
            ? "Klausur Praxis"
            : showAltKlausurenComing
            ? altKlausurenPageTitle
            : subjData?.subject?.title || ""}
        </h1>

        {showKlausurPraxis ? (
          renderKlausurPraxis()
        ) : showAltKlausurenComing ? (
          isWeeklyProfessorException ? (
            <section className="weekly-quiz-practice" aria-live="polite">
              <div className="weekly-quiz-hero">
                <div>
                  <span>Praxis-Modus</span>
                  <h2>Wochenquiz</h2>
                  <p>
                    Zwei geprüfte Quizfragen pro Woche: eine zur aktuellen Vorlesung
                    und eine zur Wiederholung der vorausgegangenen Inhalte.
                  </p>
                </div>
                <div className="weekly-quiz-hero-badge">Vom Prof. geprüft</div>
              </div>

              {altLoading ? (
                <div className="practice-empty-state">
                  <div className="practice-empty-title">Wochenquiz wird geladen...</div>
                </div>
              ) : altError ? (
                <div className="practice-empty-state">
                  <div className="practice-empty-title">Wochenquiz konnte nicht geladen werden</div>
                  <div className="practice-empty-copy">Bitte versuch es später erneut.</div>
                </div>
              ) : !weeklyProfessorVersion ? (
                <div className="weekly-quiz-empty">
                  <span>Demnächst</span>
                  <h2>Das Wochenquiz ist noch nicht veröffentlicht</h2>
                  <p>Sobald die geprüften Quizfragen freigegeben sind, erscheinen sie hier.</p>
                </div>
              ) : (
                <>
                  <div className="weekly-quiz-stack">
                    {weeklyProfessorQuestionSlots.map(renderWeeklyProfessorQuizCard)}
                  </div>

                  <div className="weekly-quiz-quality">
                    <span aria-hidden="true">✓</span>
                    <div>
                      <h3>Qualitätsversprechen</h3>
                      <p>
                        Diese Quizfragen wurden vom Lehrstuhl geprüft und orientieren sich
                        direkt an der aktuellen Vorlesungswoche.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </section>
          ) : (
          <section className="alt-klausuren-practice" aria-live="polite">
            {altLoading ? (
              <div className="practice-empty-state">
                <div className="practice-empty-title">Lade {altKlausurenPageTitle}...</div>
              </div>
            ) : altError ? (
              <div className="practice-empty-state">
                <div className="practice-empty-title">{altKlausurenPageTitle} konnte nicht geladen werden</div>
                <div className="practice-empty-copy">Bitte versuch es später erneut.</div>
              </div>
            ) : visibleAltklausuren.length === 0 && !canGenerateSharedKlausur ? (
              <div className="alt-klausuren-coming-card">
                <span className="alt-klausuren-coming-badge">Demnächst</span>
                <h2 className="alt-klausuren-coming-title">{altKlausurenPageTitle} kommt in Kürze</h2>
                <p className="alt-klausuren-coming-copy">
                  Sobald dein Team passende Klausuren veröffentlicht, erscheinen sie hier.
                </p>
              </div>
            ) : !selectedAltklausur ? (
              <div className="alt-klausuren-overview">
                <div className="alt-klausuren-practice-head">
                  <div>
                    <span>Praxis-Modus</span>
                    <h2>{altKlausurenPageTitle}</h2>
                    <p>Wähle eine Altklausur aus und starte anschließend Aufgaben oder Quizfragen.</p>
                  </div>
                  <div className="alt-klausuren-head-actions">
                    <div className="alt-klausuren-metrics">
                      <span>{visibleAltklausuren.length} Altklausuren</span>
                    </div>
                    {canGenerateSharedKlausur ? (
                      <button
                        type="button"
                        className="alt-klausuren-generate-btn"
                        onClick={handleGenerateSharedKlausur}
                        disabled={aiGenerating === "sharedKlausur" || Boolean(sharedKlausurPending)}
                        title="Neue Klausur mit LearnHut AI generieren"
                      >
                        {aiGenerating === "sharedKlausur" || sharedKlausurPending
                          ? "Klausur wird generiert..."
                          : "Klausur generieren"}
                      </button>
                    ) : null}
                  </div>
                </div>

                {sharedKlausurConfig ? renderSharedKlausurGenerationStatus() : null}

                {visibleAltklausuren.length === 0 ? (
                  <div className="klausur-praxis-empty">
                    <span>Bereit zum Start</span>
                    <h2>Noch keine Klausur</h2>
                    <p>Klicke auf „Klausur generieren“, um den ersten Praxissatz für dieses Fach zu erstellen.</p>
                  </div>
                ) : (
                  <div className="alt-klausuren-card-grid">
                    {visibleAltklausuren.map((alt) => {
                    const version = alt.versions[0];
                    const taskCount = version?.tasks?.length || 0;
                    const quizCount = version?.quizQuestions?.length || 0;
                    return (
                      <article className="alt-klausuren-list-card" key={alt.id}>
                        <div className="alt-klausuren-list-badges">
                          <span>{taskCount} Aufgaben</span>
                          <span>{quizCount} Quizfragen</span>
                          {Number(version?.timeLimitSeconds) > 0 ? (
                            <span>{Math.round(Number(version.timeLimitSeconds) / 60)} min</span>
                          ) : null}
                          {isAiGeneratedAltklausur(alt) ? renderGeneratedByLabel(alt) : null}
                          {alt.isTemporary ? <span>Temporär</span> : null}
                        </div>
                        <h2>{alt.title}</h2>
                        {alt.description ? <p>{alt.description}</p> : null}
                        <button
                          type="button"
                          className="alt-klausuren-enter-btn"
                          onClick={() => {
                            setSelectedAltId(alt.id);
                            setSelectedAltVersionId(null);
                            setAltPracticeTab("aufgaben");
                            setActiveAltTaskIndex(0);
                            setActiveAltQuizIndex(0);
                          }}
                        >
                          Altklausur öffnen
                        </button>
                      </article>
                    );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="alt-klausuren-practice-head">
                  <div>
                    <span>Praxis-Modus</span>
                    <h2>{selectedAltklausur?.title || altKlausurenPageTitle}</h2>
                    {selectedAltklausur?.description ? <p>{selectedAltklausur.description}</p> : null}
                  </div>
                  <div className="alt-klausuren-metrics">
                    <span>{selectedAltQuizzes.length} Quizfragen</span>
                    <span>{selectedAltTasks.length} Aufgaben</span>
                    {Number(selectedAltVersion?.timeLimitSeconds) > 0 ? (
                      <span>{Math.round(Number(selectedAltVersion.timeLimitSeconds) / 60)} min</span>
                    ) : null}
                    {isAiGeneratedAltklausur(selectedAltklausur) ? renderGeneratedByLabel(selectedAltklausur) : null}
                  </div>
                </div>

                <button
                  type="button"
                  className="alt-klausuren-back-btn"
                  onClick={() => {
                    setSelectedAltId(null);
                    setSelectedAltVersionId(null);
                    setActiveAltTaskIndex(0);
                    setActiveAltQuizIndex(0);
                  }}
                >
                  Zurück zur Übersicht
                </button>

                {selectedAltVersion ? (
                  <div className="lesson-practice-section alt-klausuren-panel">
                    <div className="lesson-practice-toolbar">
                      <div className="lesson-practice-tabs" role="tablist" aria-label="Altklausur Inhalte">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={altPracticeTab === "aufgaben"}
                          className={`lesson-practice-tab ${altPracticeTab === "aufgaben" ? "active" : ""}`}
                          onClick={() => setAltPracticeTab("aufgaben")}
                        >
                          Aufgaben
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={altPracticeTab === "quiz"}
                          className={`lesson-practice-tab ${altPracticeTab === "quiz" ? "active" : ""}`}
                          onClick={() => setAltPracticeTab("quiz")}
                        >
                          Quizfragen
                        </button>
                      </div>
                      <StudentGenerateButton
                        disabled
                        locked={altPracticeTab === "aufgaben"}
                        lockTitle={AI_SIMILAR_AUFGABEN_LOCK_TITLE}
                        title={
                          altPracticeTab === "aufgaben"
                            ? AI_SIMILAR_AUFGABEN_LOCK_TITLE
                            : "KI-Generierung ist aktuell nur in Lektionen verfügbar."
                        }
                      >
                        {altPracticeTab === "quiz" ? "Neue Quizfragen generieren" : "Ähnliche Aufgaben generieren"}
                      </StudentGenerateButton>
                    </div>

                    <div className="lesson-practice-panel">
                      {altPracticeTab === "aufgaben" ? (
                        selectedAltTasks.length > 0 ? (
                          <LessonTaskCarousel
                            tasks={selectedAltTasks}
                            activeIndex={activeAltTaskIndex}
                            onActiveIndexChange={setActiveAltTaskIndex}
                          />
                        ) : (
                          <div className="practice-empty-state">
                            <div className="practice-empty-title">Keine Aufgaben veröffentlicht</div>
                            <div className="practice-empty-copy">
                              Für diese Altklausur sind noch keine Aufgaben sichtbar.
                            </div>
                          </div>
                        )
                      ) : selectedAltQuizzes.length > 0 ? (
                        <AltklausurQuizCarousel
                          questions={selectedAltQuizzes}
                          activeIndex={activeAltQuizIndex}
                          onActiveIndexChange={setActiveAltQuizIndex}
                          compactSidePreviews={hasKlausurPraxis}
                          versionId={selectedAltVersion?.id || ""}
                        />
                      ) : (
                        <div className="practice-empty-state">
                          <div className="practice-empty-title">Keine Quizfragen veröffentlicht</div>
                          <div className="practice-empty-copy">
                            Für diese Altklausur sind noch keine Quizfragen sichtbar.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </section>
          )
        ) : (
          <>
            {blocks.map((b) => (
              <div
                key={b.id}
                className="subject-description lh-lessonContent lh-lessonContent-normalized"
                dangerouslySetInnerHTML={{ __html: b.html || "" }}
              />
            ))}

            {firstPublicLessonInFirstCategory && (
              <div
                className="next-lesson"
                onClick={() =>
                  onLessonSelect(
                    firstPublicLessonInFirstCategory.id,
                    firstPublicLessonInFirstCategory.title
                  )
                }
              >
                <span className="next-lesson-text">
                  {firstPublicLessonInFirstCategory.title}
                </span>
                <img src={subjectArrow} alt="→" width={18} />
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ---------------- RENDER: Lesson ----------------
  if (contentLoading)
    return <div className="subject-right-container">Loading…</div>;
  if (contentError)
    return <div className="subject-right-container">Error loading lesson.</div>;

  const quizzes = effectiveQuizzes;
  const manualQuizzes = quizzes.filter((quiz) => !isAiGeneratedQuiz(quiz));
  const aiGeneratedQuizzes = quizzes.filter(isAiGeneratedQuiz);
  const hasAiQuizSection = aiGeneratedQuizzes.length > 0 || generatedQuizPreviews.length > 0;
  const requiredQuizCount = quizzes.filter(isRequiredQuiz).length;

  const updatedAlreadyCorrect = quizState.alreadyCorrect || {};
  const allAnswered =
    quizzes.length > 0 &&
    quizzes.every(
      (q) => updatedAlreadyCorrect[q.id] || (quizState.selected?.[q.id] || []).length > 0
    );
  const allCorrect =
    allAnswered && quizzes.every((q) => updatedAlreadyCorrect[q.id]);

  // ✅ Only show feedback for student lessons with content until it is submitted
  const shouldShowFeedback =
    !isTutorOrAdmin && contents.length > 0 && !feedbackClosedForLesson;

  const feedbackIcons = [
    { id: 1, src: Feedback1, alt: "Feedback 1" },
    { id: 2, src: Feedback2, alt: "Feedback 2" },
    { id: 3, src: Feedback3, alt: "Feedback 3" },
    { id: 4, src: Feedback4, alt: "Feedback 4" },
    { id: 5, src: Feedback5, alt: "Feedback 5" },
  ];
  const practiceTabs =
    defaultPracticeTab === "quiz"
      ? [
          { id: "quiz", label: "Quizfragen" },
          { id: "aufgaben", label: "Aufgaben" },
        ]
      : [
          { id: "aufgaben", label: "Aufgaben" },
          { id: "quiz", label: "Quizfragen" },
        ];

  return (
    <div className="subject-right-wrapper" ref={contentRootRef}>
      <div className="subject-right-container">
        <h1 className="subject-title">{lessonTitle}</h1>

        {contents.map((c) => {
          const value = c.contentData;

          switch (c.contentType) {
            case "text":
              return (
                <div
                  key={c.id}
                  className="lesson-content lh-lessonContent lh-lessonContent-normalized"
                  dangerouslySetInnerHTML={{ __html: value }}
                />
              );

            case "title":
              return (
                <div
                  key={c.id}
                  className="lesson-content lh-lessonContent lh-lessonContent-normalized"
                  dangerouslySetInnerHTML={{ __html: value }}
                />
              );

            case "photo online":
              return (
                <img key={c.id} src={value} alt="" className="lesson-image" />
              );

            case "photo offline": {
              const src = offlineImages[value];
              return src ? (
                <img key={c.id} src={src} alt="" className="lesson-image" />
              ) : null;
            }

            case "youtube":
              return (
                <div key={c.id} className="lesson-video">
                  <ReactPlayer
                    url={value}
                    light
                    playing={playingVideoId === c.id}
                    controls
                    width="100%"
                    height="100%"
                    config={{ youtube: { playerVars: { autoplay: 1 } } }}
                    onClickPreview={() => setPlayingVideoId(c.id)}
                  />
                </div>
              );

            case "code":
              return (
                <div key={c.id} className="lesson-code-block">
                  <div className="code-toolbar">
                    <button
                      className="copy-button"
                      onClick={() => {
                        copy(value);
                        setCopiedBlock(c.id);
                        setTimeout(() => setCopiedBlock(null), 2000);
                      }}
                    >
                      {copiedBlock === c.id ? "✓ Copied" : "Copy"}
                    </button>
                  </div>
                  <SyntaxHighlighter
                    language="javascript"
                    style={customCodeStyle}
                    wrapLongLines
                  >
                    {value}
                  </SyntaxHighlighter>
                </div>
              );

            case "math":
              return (
                <div key={c.id} className="lesson-math-block">
                  <BlockMath math={value} />
                </div>
              );

            default:
              return null;
          }
        })}

        {/* ✅ FEEDBACK SECTION (only if there is lesson content) */}
        {shouldShowFeedback && (
          <div className="lesson-feedback-section">
            <div className="lesson-feedback-title">
              Aber vorher gib uns bitte dein Feedback zur Lektion.
            </div>

            <div className="lesson-feedback-icons">
              {feedbackIcons.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`lesson-feedback-icon-btn ${
                    feedbackRating === f.id ? "selected" : ""
                  }`}
                  onClick={() => setFeedbackRating(f.id)}
                  aria-label={`Feedback ${f.id}`}
                >
                  <img src={f.src} alt={f.alt} className="lesson-feedback-icon" />
                </button>
              ))}
            </div>

            <textarea
              ref={textareaRef}
              className="lesson-feedback-textarea"
              placeholder="Wie fandest du die Erklärung? Schreib’s hier..."
              value={feedbackText}
              onChange={(e) => {
                setFeedbackText(e.target.value);
              }}
              onInput={autoGrowTextarea}
            />

            <div className="lesson-feedback-actions">
              <button
                type="button"
                className="lesson-feedback-submit-btn"
                onClick={takeFeedback}
                disabled={feedbackSubmitting || feedbackBusy}
              >
                {feedbackSubmitting || feedbackBusy
                  ? "Wird gesendet..."
                  : "Feedback absenden"}
              </button>
            </div>

            {feedbackError && <div className="lesson-feedback-error">{feedbackError}</div>}
          </div>
        )}

        {showFeedbackToast && (
          <div className="feedback-toast">Danke dir, dein Feedback ist angekommen.</div>
        )}

        <section className="lesson-practice-section" aria-label="Üben und Vertiefen">
          <div className="lesson-practice-header">
            <p className="lesson-practice-title">Üben & Vertiefen</p>
          </div>

          <div className="lesson-practice-toolbar">
            <div className="lesson-practice-tabs" role="tablist" aria-label="Vertiefungsbereiche">
              {practiceTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={practiceTab === tab.id}
                  className={`lesson-practice-tab ${practiceTab === tab.id ? "active" : ""}`}
                  onClick={() => setPracticeTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <StudentGenerateButton
              onClick={practiceTab === "quiz" ? handleGenerateQuiz : handleGenerateSimilarAufgabe}
              loading={aiGenerating === (practiceTab === "quiz" ? "quiz" : "aufgabe")}
              locked={practiceTab === "aufgaben" && !canGenerateSimilarAufgabeForSubject}
              lockTitle={AI_SIMILAR_AUFGABEN_LOCK_TITLE}
              disabled={
                practiceTab === "quiz"
                  ? !canGenerateLessonQuiz
                  : !canGenerateSimilarAufgabe
              }
              title={
                practiceTab === "quiz"
                  ? canGenerateLessonQuiz
                    ? "Neue Quizfragen mit LearnHut AI generieren"
                    : "KI-Quizfragen sind für dieses Fach nicht konfiguriert."
                  : !canGenerateSimilarAufgabeForSubject
                  ? AI_SIMILAR_AUFGABEN_LOCK_TITLE
                  : canGenerateSimilarAufgabe
                  ? "Ähnliche Aufgabe mit LearnHut AI generieren"
                  : AI_SIMILAR_AUFGABEN_MANUAL_ONLY_TITLE
              }
            >
              {practiceTab === "quiz" ? "Neue Quizfragen generieren" : "Ähnliche Aufgaben generieren"}
            </StudentGenerateButton>
          </div>

          {(aiGenerating || aiGenerationMessage || aiGenerationError) && (
            <div className={`ai-generation-status ${aiGenerationError ? "is-error" : ""}`}>
              {aiGenerating ? (
                <span className="ai-waiting-inline">
                  {aiGenerationStepLabel}
                  <span className="ai-waiting-dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                </span>
              ) : (
                aiGenerationError || aiGenerationMessage
              )}
            </div>
          )}

          <div className="lesson-practice-panel" ref={practiceResultsRef}>
            {practiceTab === "quiz" ? (
              quizzes.length > 0 || generatedQuizPreviews.length > 0 ? (
                <>
                  {quizzes.length > 0 ? (
                    <div className="lesson-practice-subline">
                      Prüfe dein Verständnis direkt mit den vorhandenen Quizfragen.
                      {requiredQuizCount > 0 ? ` ${requiredQuizCount} davon zählen für deinen Fortschritt.` : ""}
                    </div>
                  ) : null}

                  {manualQuizzes.map((q, index) => (
                    <QuizBlock
                      key={q.id}
                      quiz={q}
                      index={index}
                      selected={quizState.selected?.[q.id]}
                      onSelect={handleSelect}
                      onOptionsLoaded={handleQuizOptionsLoaded}
                      evaluate={quizState.evaluate}
                      wasCorrect={quizState.alreadyCorrect?.[q.id]}
                    />
                  ))}

                  {hasAiQuizSection ? (
                    <div className="ai-generated-quiz-section">
                      <div className="ai-generated-quiz-separator">
                        <span />
                        <strong>KI-generierte Quizfragen</strong>
                        <span />
                      </div>
                      {aiGeneratedQuizzes.map((q, index) => (
                        <QuizBlock
                          key={q.id}
                          quiz={q}
                          index={manualQuizzes.length + index}
                          selected={quizState.selected?.[q.id]}
                          onSelect={handleSelect}
                          onOptionsLoaded={handleQuizOptionsLoaded}
                          evaluate={quizState.evaluate}
                          wasCorrect={quizState.alreadyCorrect?.[q.id]}
                        />
                      ))}
                      {generatedQuizPreviews.map((q, index) => (
                        <GeneratedQuizPreview key={q.id} quiz={q} index={index} />
                      ))}
                    </div>
                  ) : null}

                  {quizzes.length > 0 && !allCorrect && (
                    <button className="check-button practice-check-button" onClick={handleCheck}>
                      {quizState.evaluate ? "Erneut versuchen" : "Antworten prüfen"}
                    </button>
                  )}
                </>
              ) : (
                <div className="practice-empty-state">
                  <div className="practice-empty-title">Quizfragen demnächst verfügbar</div>
                  <div className="practice-empty-copy">
                    Für diese Lektion folgen in Kürze passende Quizfragen.
                  </div>
                </div>
              )
            ) : lessonTaskItems.length > 0 ? (
              <LessonTaskCarousel
                tasks={lessonTaskItems}
                activeIndex={activeTaskIndex}
                onActiveIndexChange={setActiveTaskIndex}
                groupGeneratedTasks
                showSidePreviews={false}
              />
            ) : (
              <div className="practice-empty-state">
                <div className="practice-empty-title">Aufgaben demnächst verfügbar</div>
                <div className="practice-empty-copy">
                  Für diese Lektion folgen in Kürze passende Aufgaben.
                </div>
              </div>
            )}
          </div>
        </section>

        {nextLesson && (
          <div
            className="next-lesson"
            onClick={() => onLessonSelect(nextLesson.id, nextLesson.title)}
          >
            <span className="next-lesson-text">{nextLesson.title}</span>
            <img src={subjectArrow} alt="→" width={18} />
          </div>
        )}
      </div>

      {!isTutorOrAdmin && (
        <div className="ai-fab-wrap">
          <div
            className={`ai-assistant-popover ${aiInfoOpen ? "is-visible" : ""}`}
            role="dialog"
            aria-label="LearnHut AI Assistent"
          >
            <div className="ai-assistant-topline" aria-hidden="true" />

            <div className="ai-assistant-head">
              <div className="ai-assistant-head-main">
                <div className="ai-assistant-head-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" className="ai-assistant-head-svg">
                    <path
                      d="M12 3a9 9 0 0 0-9 9v5h3v4l4-4h2a9 9 0 0 0 0-18Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="ai-assistant-head-title">LearnHut AI</h3>
                  <p className="ai-assistant-head-subtitle">
                    {canUseSubjectChat ? currentAiSubject.title : "Noch nicht konfiguriert"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="ai-assistant-close"
                onClick={() => setAiInfoOpen(false)}
                aria-label="KI-Assistent schließen"
              >
                ×
              </button>
            </div>

            <div className="ai-assistant-tabs" role="tablist" aria-label="KI-Modus">
              <button type="button" className="active" role="tab" aria-selected="true">
                Kontext-Chat
              </button>
              <button type="button" role="tab" aria-selected="false" disabled>
                Quellen
              </button>
            </div>

            <p className="ai-assistant-copy">
              {canUseSubjectChat
                ? "Die Unterhaltung bleibt pro Fach und Sitzung getrennt. Hochgeladene Dateien werden als Zusatzkontext an die AI gesendet."
                : "LearnHut AI ist für dieses Fach noch nicht konfiguriert."}
            </p>

            {canUseSubjectChat ? (
              <form
                className={`ai-chat-panel ${aiChatDropActive ? "is-drop-active" : ""}`}
                onSubmit={handleSubjectChatSubmit}
                onDragEnter={handleAiChatDragEnter}
                onDragOver={handleAiChatDragOver}
                onDragLeave={handleAiChatDragLeave}
                onDrop={handleAiChatDrop}
              >
                <div className="ai-chat-history" aria-live="polite">
                  {aiChatHistory.length === 0 ? (
                    <div className="ai-chat-empty">
                      <div className="ai-chat-message is-assistant">
                        <span className="ai-chat-role">LearnHut AI</span>
                        <p>
                          Ich nutze das Material zu {currentAiSubject.title}. Lade gern eine Aufgabe,
                          ein Bild oder eine PDF hoch, wenn ich genau diesen Kontext beachten soll.
                        </p>
                      </div>
                      <ul className="ai-assistant-list">
                        {AI_PREVIEW_BULLETS.map((item) => (
                          <li key={item} className="ai-assistant-list-item">
                            <span className="ai-assistant-list-dot" aria-hidden="true">
                              ✓
                            </span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    aiChatHistory.map((entry, index) => (
                      <div
                        className={`ai-chat-message ${entry.role === "user" ? "is-user" : "is-assistant"}`}
                        key={`${entry.role}-${index}`}
                      >
                        <span className="ai-chat-role">
                          {entry.role === "user" ? "Du" : "LearnHut AI"}
                        </span>
                        {entry.role === "assistant" ? (
                          <MarkdownMathContent content={entry.content} />
                        ) : (
                          <p className="ai-chat-answer-text">{entry.content}</p>
                        )}
                        {entry.attachments?.length > 0 ? (
                          <div className="ai-chat-attachments">
                            {entry.attachments.map((attachment, attachmentIndex) => (
                              <span key={`${index}-attachment-${attachmentIndex}`}>
                                {typeof attachment === "string" ? attachment : attachment?.name || "Upload"}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        {entry.role === "assistant" && entry.sources?.length > 0 ? (
                          <div className="ai-chat-sources">
                            <strong>Quellen</strong>
                            {entry.sources.slice(0, 3).map((source, sourceIndex) => (
                              <small key={`${index}-source-${sourceIndex}`}>
                                {source?.source || "Kursmaterial"}
                                {source?.page ? `, S. ${source.page}` : ""}
                                {source?.topic ? ` · ${source.topic}` : ""}
                              </small>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ))
                  )}
                  {aiChatLoading ? (
                    <div className="ai-chat-message is-assistant ai-chat-waiting-card">
                      <span className="ai-chat-role">LearnHut AI</span>
                      <p>
                        {aiChatStepLabel}
                        <span className="ai-waiting-dots" aria-hidden="true">
                          <span />
                          <span />
                          <span />
                        </span>
                      </p>
                    </div>
                  ) : null}
                  <div ref={aiChatHistoryEndRef} />
                </div>

                {aiChatError ? <div className="ai-chat-error">{aiChatError}</div> : null}

                <input
                  ref={aiChatFileInputRef}
                  className="ai-chat-file-input"
                  type="file"
                  accept={AI_UPLOAD_ACCEPT}
                  multiple
                  onChange={handleAiChatUploadChange}
                  disabled={aiChatLoading}
                />

                {aiChatUploads.length > 0 ? (
                  <div className="ai-chat-upload-list">
                    {aiChatUploads.map((file, index) => (
                      <button
                        key={`${file.name}-${file.size}-${index}`}
                        type="button"
                        onClick={() => removeAiChatUpload(index)}
                        title="Upload entfernen"
                      >
                        <span>{file.name}</span>
                        {formatAiUploadSize(file) ? <small>{formatAiUploadSize(file)}</small> : null}
                        <b aria-hidden="true">×</b>
                      </button>
                    ))}
                  </div>
                ) : null}

                <div className="ai-chat-input-row">
                  <button
                    type="button"
                    className="ai-chat-attach-btn"
                    onClick={() => aiChatFileInputRef.current?.click()}
                    disabled={aiChatLoading}
                    aria-label="Dateien hochladen"
                    title="Dateien hochladen"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M7.8 12.4 13 7.2a3 3 0 0 1 4.3 4.2l-6.2 6.2a4.5 4.5 0 0 1-6.4-6.4l7-7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <input
                    value={aiChatInput}
                    onChange={(event) => setAiChatInput(event.target.value)}
                    placeholder="Frage stellen oder Material hochladen..."
                    disabled={aiChatLoading}
                  />
                  <button
                    type="submit"
                    className="ai-chat-send-btn"
                    disabled={aiChatLoading || (!aiChatInput.trim() && aiChatUploads.length === 0)}
                    aria-label="Nachricht senden"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M4 12 20 4l-5 16-3-7-8-1Z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                <div className="ai-chat-suggestions">
                  {AI_CHAT_SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => setAiChatInput(suggestion)}
                      disabled={aiChatLoading}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </form>
            ) : (
              <div className="ai-assistant-coming-box">
                <p>Für dieses Fach nicht verfügbar</p>
              </div>
            )}
          </div>

          <button
            type="button"
            className={`ai-fab-button ${aiInfoOpen ? "is-open" : ""}`}
            onClick={() => setAiInfoOpen((prev) => !prev)}
            aria-expanded={aiInfoOpen}
            aria-label="KI-Assistent Vorschau anzeigen"
          >
            <svg viewBox="0 0 24 24" className="ai-fab-icon" aria-hidden="true">
              <path
                d="M5 6.5h14a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19 17.5H10l-4.5 3v-3H5A1.5 1.5 0 0 1 3.5 16V8A1.5 1.5 0 0 1 5 6.5Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="ai-fab-badge" aria-hidden="true">
              <span className="ai-fab-badge-ping" />
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
